import { validateToolFeedback } from './toolFeedbackValidation';
import MessageRulesEditor from './MessageRulesEditor';
import ToolAvailability from './ToolAvailability';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Trash, Upload, Download, AudioLines, ShieldCheck, Brain, Mic, Volume2, Database, PhoneOff, PhoneForwarded, Timer, MessageSquare, Hand, Voicemail, Hash, BarChart3 } from 'lucide-react';
import ModernTimeDropdown from '../../../dashboard-components/layouts/modernDropdown';
import ModernMultiSelect from '../../../dashboard-components/layouts/ModernMultiSelect.jsx';
import { FaTimes } from 'react-icons/fa';
import ExploreVoicesModal from '../../layouts/ExploreVoicesModal.jsx';
import server from '../../../api/server.js';
import { useToast } from '../../../context/ToastContext.jsx';
import { TextareaWithVariables } from './VariableInsertion.jsx'; // Import the new component
import VariableConfigurationTable from './VariableConfigurationTable.jsx';
import CustomToolsConfig from './CustomToolsConfig.jsx';
import StandardToolsConfig from './StandardToolsConfig.jsx';
import InbuiltAnalyticsConfig from './InbuiltAnalyticsConfig.jsx';
import FutureSchedulingConfig, {
  createDefaultFutureSchedulingConfig,
  getFutureSchedulingAnalyticsFields,
  normalizeFutureSchedulingConfig,
  validateFutureSchedulingConfig,
} from './FutureSchedulingConfig.jsx';
import MessageWithOverrides from './MessageWithOverrides';
import { normalizeLanguageKey, getMessageRules, getAgentMessageRules, setAgentMessageRules, validateMessageRules, normalizeAgentMessageRules } from './messageRules';
import ChatSettings from './ChatSettings.jsx';
import { isLiveKitBrowserCall, startLiveKitBrowserCall } from './browserCallLiveKit.js';
import { normalizeChat, changeAgentPipeline, canTestChat, validateChat, buildChatTestTarget } from './chatConfiguration.js';
import { getConfigurationErrors } from '../../../utils/configurationErrors.js';
import { ErrorSummary } from '../../layouts/ConfigurationFormControls';
import TestChatModal from '../../chat/TestChatModal.jsx';
import DeployConfirmationModal from '../../../dashboard-components/layouts/DeployConfirmationModal.jsx';
import ConfigurationDiffButton from '../../../dashboard-components/layouts/ConfigurationDiffButton.jsx';
import { getApiToastMessage } from '../../../utils/apiToastMessage.js';
import { normalizeConversationControls } from './conversationControls.js';
import {
  getAiCousticsCapability,
  getAiCousticsModelFromLabel,
  getAiCousticsModelLabel,
  getNoiseCancellationCapability,
  getNoiseCancellationProcessorOptions,
  modelSupportsSensitivity,
  normalizeNoiseCancellation,
  setNoiseCancellationProcessor,
  setTelephonyNoiseCancellation,
} from './noiseCancellation.js';

const getUploadWarning = (configuration, targetAgentId, latestVersion) => {
  const source = configuration._export_metadata;
  if (!source?.agent_id || !Number.isInteger(source.version)) {
    return 'Source version unknown. This file has no source version metadata and may overwrite newer settings.';
  }
  if (targetAgentId && source.agent_id !== targetAgentId) {
    return 'This file comes from another agent. Continuing will copy its settings into the current agent.';
  }
  if (targetAgentId && source.version < latestVersion) {
    return `This file is v${source.version}; the current version is v${latestVersion}. Uploading may overwrite newer settings.`;
  }
  return null;
};

const formatModifiedTime = (timestamp, now = Date.now()) => {
  const modified = new Date(timestamp * 1000).getTime();
  if (!timestamp || !Number.isFinite(modified)) return 'Unavailable';
  const seconds = Math.max(0, Math.floor((now - modified) / 1000));
  if (seconds < 60) return 'just now';
  const units = [[31536000, 'year'], [2592000, 'month'], [86400, 'day'], [3600, 'hour'], [60, 'minute']];
  const [size, unit] = units.find(([size]) => seconds >= size);
  const count = Math.floor(seconds / size);
  return `${count} ${unit}${count === 1 ? '' : 's'} ago`;
};

function ConfigurationModifiedTime({ metadata, label }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);
  const timestamp = metadata?.updated_at;
  const modifiedDate = new Date(timestamp * 1000);
  const valid = timestamp && Number.isFinite(modifiedDate.getTime());
  const exactTime = valid ? `${modifiedDate.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST` : undefined;
  return (
    <p className="text-gray-500 mt-1 text-xs" title={exactTime}>
      {label}: <time dateTime={valid ? modifiedDate.toISOString() : undefined}>{formatModifiedTime(timestamp, now)}</time>
      {metadata?.updated_by && ` · by ${metadata.updated_by}`}
    </p>
  );
}

const AGENT_DETAILS_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'pipeline', label: 'Pipeline' },
  { id: 'chat_controls', label: 'Chat Controls' },
  { id: 'tools', label: 'Tools' },
  { id: 'conversation_controls', label: 'Conversation Controls' },
  { id: 'inbuilt_analytics', label: 'Inbuilt Analytics' },
  { id: 'future_scheduling', label: 'Future Scheduling' },
];

const getConfigurationTab = (path) => {
  const fields = String(path).split('.');
  if (fields.includes('chat')) return 'chat_controls';
  if (fields.includes('inbuilt_analytics')) return 'inbuilt_analytics';
  if (fields.includes('future_scheduling')) return 'future_scheduling';
  if (fields.some(field => ['custom_tools', 'knowledge_base', 'conversation_controls'].includes(field))) return 'tools';
  if (fields.some(field => [
    'pipeline_type', 'voice_to_voice', 'llm', 'transcription', 'voice', 'noise_cancellation',
    'vad_params', 'post_vad_wait_for_user', 'smart_turn_detection', 'llm_turn_detection', 'background_effects',
  ].includes(field))) return 'pipeline';
  if (fields.some(field => [
    'conversation_hangup', 'call_transfer', 'conversation_termination', 'conversation_turn_timeout',
    'interruptions', 'voice_mail', 'dtmf_enabled', 'dtmf_idle_timeout_seconds',
    'post_call_analytics_enabled', 'post_call_analytics_service_id', 'post_call_analytics_transcription_mode',
  ].includes(field))) return 'conversation_controls';
  return 'overview';
};

const CONFIGURATION_SECTIONS = {
  'Turn Detection': { icon: AudioLines, description: 'Configure speech detection and turn timing' },
  'Noise Cancellation': { icon: ShieldCheck, description: 'Configure background noise reduction' },
  'Base Model': { icon: Brain, description: 'Configure the language model and reasoning' },
  'Voice to Voice': { icon: AudioLines, description: 'Configure the voice model and provider' },
  'Transcription': { icon: Mic, description: 'Configure speech recognition and transcription' },
  'Voice': { icon: Volume2, description: 'Configure speech output and background sound' },
  'Knowledge Base': { icon: Database, description: 'Configure knowledge sources and retrieval' },
  'Conversation Hangup': { icon: PhoneOff, description: 'Configure when the agent ends a conversation' },
  'Conversation Turn': { icon: MessageSquare, description: 'Configure inactivity reminders and attempts' },
  'Call Transfer': { icon: PhoneForwarded, description: 'Configure call transfer behavior and messages' },
  'Conversation Termination': { icon: Timer, description: 'Configure maximum conversation duration' },
  'Interruptions': { icon: Hand, description: 'Configure when users can interrupt the agent' },
  'Voice Mail': { icon: Voicemail, description: 'Configure voicemail detection and handling' },
  'DTMF': { icon: Hash, description: 'Configure keypad input and idle timeout' },
  'Post Call Analytics': { icon: BarChart3, description: 'Configure analytics processing after conversations' },
};

const ConfigurationSection = ({ title, children, hasErrors = false }) => {
  const { icon: Icon, description } = CONFIGURATION_SECTIONS[title];
  return (
    <details className="group border-t pt-6" open={hasErrors}>
      <summary className="flex cursor-pointer list-none items-center gap-3 rounded-lg p-2 transition-colors hover:bg-gray-50 [&::-webkit-details-marker]:hidden">
        <span className="flex shrink-0 items-center gap-2">
          <ChevronRight aria-hidden="true" className="h-5 w-5 text-gray-600 transition-transform group-open:rotate-90" />
          <Icon aria-hidden="true" className="h-5 w-5 text-indigo-600" />
        </span>
        <div>
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
      </summary>
      <div className="mt-4">{children}</div>
    </details>
  );
};

const normalizeLlmReasoningConfig = (llm, allowedConfig = {}) => {
  const normalizedLlm = { ...(llm || {}) };
  const modelConfig = allowedConfig?.llm?.[normalizedLlm.provider]?.[normalizedLlm.model];
  const reasoningConfig = modelConfig?.reasoning_effort;

  if (reasoningConfig?.available !== 1) {
    delete normalizedLlm.reasoning_effort;
    return normalizedLlm;
  }

  const options = Array.isArray(reasoningConfig.options)
    ? reasoningConfig.options.map(option => String(option).trim().toLowerCase())
    : [];
  const defaultValue = String(reasoningConfig.default_value || 'none').trim().toLowerCase();
  const currentValue = String(normalizedLlm.reasoning_effort || '').trim().toLowerCase();
  normalizedLlm.reasoning_effort = options.includes(currentValue) ? currentValue : defaultValue;
  return normalizedLlm;
};

const InputWithCharacterCount = ({ 
  label, 
  value, 
  onChange, 
  maxLength, 
  placeholder = '', 
  disabled = false,
  required = false,
  type = 'text',
  className = ''
}) => {
  return (
    <div className="relative">
      <label className={`block text-sm font-medium text-gray-700 mb-2 ${required ? "after:content-['*'] after:text-red-500 after:ml-1" : ''}`}>
        {label}
      </label>
      <div className="relative">
        <input
          type={type}
          value={value || ''}
          onChange={onChange}
          maxLength={maxLength}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500 pr-16 ${disabled ? 'bg-gray-50 text-gray-600' : ''} ${className}`}
        />
        {maxLength && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
            {(value || '').length}/{maxLength}
          </div>
        )}
      </div>
    </div>
  );
};

// Character count textarea component
const TextareaWithCharacterCount = ({ 
  label, 
  value, 
  onChange, 
  maxLength, 
  placeholder = '', 
  disabled = false,
  required = false,
  rows = 3,
  className = ''
}) => {
  return (
    <div className="relative">
      <label className={`block text-sm font-medium text-gray-700 mb-2 ${required ? "after:content-['*'] after:text-red-500 after:ml-1" : ''}`}>
        {label}
      </label>
      <div className="relative">
        <textarea
          value={value || ''}
          onChange={onChange}
          rows={rows}
          maxLength={maxLength}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500 resize-vertical ${disabled ? 'bg-gray-50 text-gray-600' : ''} ${className}`}
          style={{ minHeight: `${rows * 1.5}rem` }}
        />
        {maxLength && (
          <div className="absolute bottom-2 right-2 text-xs text-gray-400 bg-white bg-opacity-90 px-1 rounded">
            {(value || '').length}/{maxLength}
          </div>
        )}
      </div>
    </div>
  );
};

const normalizeBooleanFlag = (value, defaultValue = false) => {
  if (value === true || value === false) return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['yes', 'true', '1'].includes(normalized)) return true;
    if (['no', 'false', '0'].includes(normalized)) return false;
  }
  return defaultValue;
};


const AgentDetails = ({ clientID, serviceID }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { agentId } = useParams();
  const location = useLocation();
  const [variableConfigs, setVariableConfigs] = useState({});
  const [systemVariables, setSystemVariables] = useState([]);
  const [countryCode, setCountryCode] = useState('+91 (INDIA)');

  const [client_id, setClient_id] = useState(null);
  const [service_id, setService_id] = useState(null);
  const [email, setEmail] = useState(null);
  const [role, setRole] = useState('admin');

  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Agent state
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [deployedStatus, setDeployedStatus] = useState(false);
  const [changesOngoing, setChangesOngoing] = useState(false);
  const [showDeployConfirmation, setShowDeployConfirmation] = useState(false);
  const [uploadConfirmation, setUploadConfirmation] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('Live Version');
  const [current_version, setCurrent_version] = useState(0);
  const [liveMetadata, setLiveMetadata] = useState(null);
  const [draftMetadata, setDraftMetadata] = useState(null);
  const [agentDetailsTab, setAgentDetailsTab] = useState('overview');
  const [activeAgentOptions, setActiveAgentOptions] = useState([]);

  // Add baseline data states
  const [liveAgentData, setLiveAgentData] = useState(null); // Stores the live/deployed version
  const [draftAgentData, setDraftAgentData] = useState(null); // Stores the draft version
  const [baselineAgent, setBaselineAgent] = useState(null); // Current baseline for comparison

  // Variable state management
  const [dynamicVariables, setDynamicVariables] = useState([]);

  // Allowed config and options
  const [configData, setConfigData] = useState({ voice: {}, llm: {}, transcription: {}, background_effects: {}, voice_to_voice: {}, noise_cancellation: {}, post_call_analytics_transcription_mode: [] });
  const [v2vProviderOptions, setV2vProviderOptions] = useState([]);
  const [v2vModelOptions, setV2vModelOptions] = useState([]);
  const [v2vVoiceOptions, setV2vVoiceOptions] = useState([]);
  const [selectedV2vGender, setSelectedV2vGender] = useState('female');
  const [languageOptions, setLanguageOptions] = useState([]);
  const [llmProviderOptions, setLlmProviderOptions] = useState([]);
  const [llmModelOptions, setLlmModelOptions] = useState([]);
  const [transcriptionProviderOptions, setTranscriptionProviderOptions] = useState([]);
  const [transcriptionModelOptions, setTranscriptionModelOptions] = useState([]);
  const [voiceProviderOptions, setVoiceProviderOptions] = useState([]);
  const [voiceModelOptions, setVoiceModelOptions] = useState([]);
  const [voiceOptions, setVoiceOptions] = useState([]);
  const [secondaryLanguageOptions, setSecondaryLanguageOptions] = useState([]);
  const [voiceGenderOptions] = useState(['female', 'male']);
  const [selectedVoiceGender, setSelectedVoiceGender] = useState('female');
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [knowledgeBaseNames, setKnowledgeBaseNames] = useState([]);
  const [knowledgeBaseMap, setKnowledgeBaseMap] = useState([]);

  // Test workflow + voices modal
  const [testWorkflowOption, setTestWorkflowOption] = useState('');
  const [showOutboundModal, setShowOutboundModal] = useState(false);
  const [testChatTarget, setTestChatTarget] = useState(null);
  const [testChatOpen, setTestChatOpen] = useState(false);
  const [testChatRun, setTestChatRun] = useState(0);
  const [isChatTestActive, setIsChatTestActive] = useState(false);
  const [isCallStarting, setIsCallStarting] = useState(false);
  const callStartingRef = useRef(false);
  const [configurationErrors, setConfigurationErrors] = useState({});
  const hasConfigurationError = (...fields) => Object.keys(configurationErrors).some(path =>
    path.split('.').some(field => fields.includes(field))
  );

  useEffect(() => {
    setTestChatTarget(null);
    setTestChatOpen(false);
    setIsChatTestActive(false);
  }, [client_id, email, agentId]);
  const [outboundPhoneNumber, setOutboundPhoneNumber] = useState('');
  const [showVoicesModal, setShowVoicesModal] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  // WebRTC state/refs
  const [isCallActive, setIsCallActive] = useState(false);
  const [callStatus, setCallStatus] = useState('');
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingInfo, setRecordingInfo] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const localStreamRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const livekitClientRef = useRef(null);
  const audioElementRef = useRef(null);
  const recordingIntervalRef = useRef(null);

  const [serviceOptions, setServiceOptions] = useState([]);

    // Character limits configuration
    const CHARACTER_LIMITS = {
      AGENT_NAME: 100,
      DESCRIPTION: 500,
      COMPANY: 200,
      FIRST_MESSAGE: 1000, 
      SYSTEM_PROMPT: 100000,
      KEYWORDS: 10000,
      HANGUP_INSTRUCTION: 5000,
      HANGUP_MESSAGE: 500,
      TERMINATION_MESSAGE: 500,
      TURN_TIMEOUT_MESSAGE: 500,
      VOICE_MAIL_MESSAGE: 300,
      LLM_TURN_DETECTION_INSTRUCTION: 3000
    };

  const VAD_DEFAULTS = {
    confidence: 0.7,
    start_secs: 0.2,
    stop_secs: 0.7,
    min_volume: 0.6
  };
  const VAD_FIELD_CONFIG = [
    { key: 'confidence', label: 'Confidence' },
    { key: 'start_secs', label: 'Start Seconds' },
    { key: 'stop_secs', label: 'Stop Seconds' },
    { key: 'min_volume', label: 'Min Volume' }
  ];
  const TRANSCRIPTION_CONTROL_DEFAULTS = {
    pre_speech_audio_buffer_ms: 200,
    end_of_speech_silence_ms: 600,
    speech_start_sensitivity: 'high',
    speech_end_sensitivity: 'low'
  };
  const POST_VAD_WAIT_DEFAULTS = {
    enabled: 'No',
    secs: 0.2
  };
  const SMART_TURN_DEFAULTS = {
    enabled: 'No',
    smart_turn_timeout: 0.3
  };
  const LLM_TURN_DETECTION_DEFAULTS = {
    enabled: 'No',
    instruction: '',
    incomplete_short_timeout: null,
    short_timeout_restart_instruction: '',
    incomplete_long_timeout: null,
    long_timeout_restart_instruction: ''
  };

  const normalizeSelectedLlm = (llm, allowedConfig = configData) => (
    normalizeLlmReasoningConfig(llm, allowedConfig)
  );

  // Inbuilt Analytics conversion helpers
  const normalizeAnalyticsOutputTypeForDisplay = (backendType) => {
    const t = backendType || 'Yes/No/NA';
    if (t === 'Categorical') return 'Categorical - Single Value';
    if (typeof t === 'string' && t.startsWith('Date and Time')) return 'Date and Time';
    return t;
  };

  const normalizeAnalyticsOutputTypeForBackend = (displayType) => {
    const t = displayType || 'Yes/No/NA';
    if (t === 'Categorical - Single Value') return 'Categorical';
    if (typeof t === 'string' && t.startsWith('Date and Time')) return 'Date and Time';
    return t;
  };
  const normalizeAnalyticsStatus = (value, fallback = 'active') => {
    const normalized = String(value || fallback).trim().toLowerCase();
    return normalized === 'inactive' ? 'inactive' : 'active';
  };

  const slugifyAnalyticsKey = (name) => {
    try {
      const s = String(name || '').toLowerCase();
      return s
        .replace(/[\s\-]+/g, '_')        // spaces and hyphens to underscore
        .replace(/[^a-z0-9_]/g, '')      // remove non word chars
        .replace(/_+/g, '_')             // collapse multiple underscores
        .replace(/^_+|_+$/g, '');        // trim underscores
    } catch {
      return '';
    }
  };
  const analyticsListFromObject = (obj, type) => {
    if (!obj || typeof obj !== 'object') return [];
    const entries = Array.isArray(obj)
      ? obj.map((value, index) => [value?.display_name || value?.parameter_name || value?.name || `${type}_${index}`, value])
      : Object.entries(obj);
    return entries.map(([key, value]) => {
      const v = value || {};
      if (type === 'parameter') {
        return {
          display_name: v.display_name ?? v.parameter_name ?? v.name ?? key.replace(/_/g, ' '),
          logic: v.logic ?? '',
          subparameters_used: Array.isArray(v.subparameters_used) ? v.subparameters_used : [],
          output_type: v.output_type ?? 'Number',
          possible_values: Array.isArray(v.possible_values) ? v.possible_values : (v.output_type === 'Yes/No/NA' ? ['Yes', 'No', 'NA'] : []),
          status: normalizeAnalyticsStatus(v.status, 'active'),
          fatal: Number.isFinite(Number(v?.fatal)) ? Number(v.fatal) : 0,
          weightage: Number.isFinite(Number(v?.weightage)) ? Number(v.weightage) : 0,
          threshold_score: Number.isFinite(Number(v?.threshold_score)) ? Number(v.threshold_score) : 0,
          logic_explanation: v.logic_explanation ?? ''
        };
      }
      const isSystem = v.system === 1 || v.system === true;
      return {
        display_name: v.display_name ?? key.replace(/_/g, ' '),
        ...(v.subparameter_type === 'logic_based' ? {
          subparameter_type: 'logic_based', logic: v.logic || '',
          execution_order: v.execution_order, subparameters_used: Array.isArray(v.subparameters_used) ? v.subparameters_used : [],
          ...(Object.prototype.hasOwnProperty.call(v, 'default_value') ? { default_value: v.default_value } : {}),
        } : { instruction: v.instruction ?? '', context_scope: v.context_scope || 'conversation' }),
        output_type: normalizeAnalyticsOutputTypeForDisplay(v.output_type),
        possible_values: Array.isArray(v.possible_values) ? v.possible_values : [],
        ...(isSystem ? {} : { status: normalizeAnalyticsStatus(v.status, 'active') }),
        system: isSystem ? 1 : 0
      };
    });
  };
  const analyticsSystemSubparametersFromConfig = (allowedConfig = configData) => {
    const definitions = allowedConfig?.inbuilt_analytics_system_parameters;
    if (!definitions || typeof definitions !== 'object' || Array.isArray(definitions)) return [];
    return Object.entries(definitions).map(([key, value]) => ({
      display_name: value?.display_name ?? key,
      instruction: value?.instruction ?? '',
      output_type: normalizeAnalyticsOutputTypeForDisplay(value?.output_type),
      possible_values: Array.isArray(value?.possible_values) ? value.possible_values : [],
      system: 1
    }));
  };
  const mergeAnalyticsSystemSubparameters = (config, allowedConfig = configData) => {
    const current = config && typeof config === 'object' ? config : {};
    const previousNames = new Set((current.parameters || []).map((item) => `prev_${slugifyAnalyticsKey(item?.display_name)}`));
    const subparameters = (Array.isArray(current.subparameters) ? current.subparameters : [])
      .filter((item) => !([1, true, '1'].includes(item?.system) && slugifyAnalyticsKey(item?.display_name).startsWith('prev_'))
        || previousNames.has(slugifyAnalyticsKey(item?.display_name)));
    const existingKeys = new Set(subparameters.map((item) => slugifyAnalyticsKey(item?.display_name)).filter(Boolean));
    const missingSystemSubparameters = analyticsSystemSubparametersFromConfig(allowedConfig)
      .filter((item) => {
        const key = slugifyAnalyticsKey(item.display_name);
        return key && !existingKeys.has(key);
      });
    return {
      ...current,
      run_for_not_connected: current.run_for_not_connected === true,
      run_for_non_meaningful: current.run_for_non_meaningful === true,
      subparameters: [...subparameters, ...missingSystemSubparameters],
      parameters: Array.isArray(current.parameters) ? current.parameters : []
    };
  };
  const analyticsConfigFromObject = (obj, allowedConfig = configData) => {
    const runFlags = {
      run_for_not_connected: obj?.run_for_not_connected === true || obj?.run_for_not_connected_calls === true,
      run_for_non_meaningful: obj?.run_for_non_meaningful === true || obj?.run_for_non_meaningful_calls === true,
    };
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      return mergeAnalyticsSystemSubparameters({ ...runFlags, subparameters: [], parameters: [] }, allowedConfig);
    }
    if (obj.subparameters || obj.parameters) {
      return mergeAnalyticsSystemSubparameters({
        ...runFlags,
        subparameters: analyticsListFromObject(obj.subparameters, 'subparameter'),
        parameters: analyticsListFromObject(obj.parameters, 'parameter')
      }, allowedConfig);
    }
    // Backward compatibility with older shape where inbuilt_analytics only stored parameter-like definitions.
    return mergeAnalyticsSystemSubparameters({
      ...runFlags,
      subparameters: analyticsListFromObject(Object.fromEntries(Object.entries(obj)
        .filter(([key]) => !['run_for_not_connected', 'run_for_non_meaningful', 'run_for_not_connected_calls', 'run_for_non_meaningful_calls'].includes(key))), 'subparameter'),
      parameters: []
    }, allowedConfig);
  };
  const analyticsObjectFromList = (arr, type) => {
    const result = {};
    if (!Array.isArray(arr)) return result;
    for (const item of arr) {
      const displayName = (item?.display_name || '').trim();
      if (!displayName) continue;
      let key = slugifyAnalyticsKey(displayName);
      if (!key) continue;
      let suffix = 1;
      let candidate = key;
      while (result[candidate]) {
        suffix += 1;
        candidate = `${key}_${suffix}`;
      }
      if (type === 'parameter') {
        result[candidate] = {
          display_name: displayName,
          logic: item?.logic || '',
          subparameters_used: Array.isArray(item?.subparameters_used) ? item.subparameters_used : [],
          output_type: item?.output_type || 'Number',
          possible_values: Array.isArray(item?.possible_values) ? item.possible_values : (item?.output_type === 'Yes/No/NA' ? ['Yes', 'No', 'NA'] : []),
          status: normalizeAnalyticsStatus(item?.status, 'active'),
          fatal: Number.isFinite(Number(item?.fatal)) ? Number(item.fatal) : 0,
          weightage: Number.isFinite(Number(item?.weightage)) ? Number(item.weightage) : 0,
          threshold_score: Number.isFinite(Number(item?.threshold_score)) ? Number(item.threshold_score) : 0,
          logic_explanation: item?.logic_explanation || ''
        };
      } else {
        const isSystem = item?.system ? 1 : 0;
        result[candidate] = {
          display_name: displayName,
          ...(item?.subparameter_type === 'logic_based' ? {
            subparameter_type: 'logic_based', logic: item.logic || '',
            execution_order: item.execution_order, subparameters_used: Array.isArray(item.subparameters_used) ? item.subparameters_used : [],
            ...(Object.prototype.hasOwnProperty.call(item, 'default_value') ? { default_value: item.default_value } : {}),
          } : { instruction: item?.instruction || '', context_scope: item?.context_scope || 'conversation' }),
          output_type: normalizeAnalyticsOutputTypeForBackend(item?.output_type),
          possible_values: Array.isArray(item?.possible_values) ? item.possible_values : [],
          ...(isSystem ? {} : { status: normalizeAnalyticsStatus(item?.status, 'active') }),
          system: isSystem
        };
      }
    }
    return result;
  };
  const analyticsObjectFromConfig = (config) => ({
    run_for_not_connected: config?.run_for_not_connected === true,
    run_for_non_meaningful: config?.run_for_non_meaningful === true,
    subparameters: analyticsObjectFromList(mergeAnalyticsSystemSubparameters(config).subparameters, 'subparameter'),
    parameters: analyticsObjectFromList(config?.parameters, 'parameter')
  });
  const normalizeVadValue = (value, fallback) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    if (parsed < 0) return 0;
    if (parsed > 1) return 1;
    return parsed;
  };
  const normalizeYesNo = (value, fallback = 'No') => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    const s = String(value ?? '').trim().toLowerCase();
    if (s === 'yes') return 'Yes';
    if (s === 'no') return 'No';
    return fallback === 'Yes' ? 'Yes' : 'No';
  };
  const normalizeSmartTurnTimeout = (value, fallback = SMART_TURN_DEFAULTS.smart_turn_timeout) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    if (parsed < 0) return 0;
    if (parsed > 3) return 3;
    return parsed;
  };
  const normalizeVadParams = (vadParams) => {
    const source = (vadParams && typeof vadParams === 'object') ? vadParams : {};
    return {
      confidence: normalizeVadValue(source.confidence, VAD_DEFAULTS.confidence),
      start_secs: normalizeVadValue(source.start_secs, VAD_DEFAULTS.start_secs),
      stop_secs: normalizeVadValue(source.stop_secs, VAD_DEFAULTS.stop_secs),
      min_volume: normalizeVadValue(source.min_volume, VAD_DEFAULTS.min_volume)
    };
  };
  const normalizeTranscriptionControlInteger = (value, fallback, min, max) => {
    if (value === null || value === undefined || String(value).trim() === '') return fallback;
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < min || parsed > max) return fallback;
    return parsed;
  };
  const normalizeTranscriptionControlSensitivity = (value, fallback) => {
    const normalized = String(value || '').trim().toLowerCase();
    return ['high', 'low'].includes(normalized) ? normalized : fallback;
  };
  const normalizeTranscriptionControls = (transcriptionControls) => {
    const source = (transcriptionControls && typeof transcriptionControls === 'object') ? transcriptionControls : {};
    return {
      pre_speech_audio_buffer_ms: normalizeTranscriptionControlInteger(
        source.pre_speech_audio_buffer_ms,
        TRANSCRIPTION_CONTROL_DEFAULTS.pre_speech_audio_buffer_ms,
        0,
        2000
      ),
      end_of_speech_silence_ms: normalizeTranscriptionControlInteger(
        source.end_of_speech_silence_ms,
        TRANSCRIPTION_CONTROL_DEFAULTS.end_of_speech_silence_ms,
        100,
        3000
      ),
      speech_start_sensitivity: normalizeTranscriptionControlSensitivity(
        source.speech_start_sensitivity,
        TRANSCRIPTION_CONTROL_DEFAULTS.speech_start_sensitivity
      ),
      speech_end_sensitivity: normalizeTranscriptionControlSensitivity(
        source.speech_end_sensitivity,
        TRANSCRIPTION_CONTROL_DEFAULTS.speech_end_sensitivity
      )
    };
  };
  const getActiveRealtimeModelConfig = (agent, allowedConfig = configData) => {
    if (agent?.pipeline_type === 'voice_to_voice') {
      return allowedConfig?.voice_to_voice?.[agent?.voice_to_voice?.provider]?.[agent?.voice_to_voice?.model];
    }
    return allowedConfig?.transcription?.[agent?.transcription?.provider]?.[agent?.transcription?.model];
  };
  const supportsTranscriptionControls = (agent, allowedConfig = configData) => (
    getActiveRealtimeModelConfig(agent, allowedConfig)?.supports_transcription_controls === true
  );
  const isSmartTurnSupportedForLanguage = (lang) => {
    const key = normalizeLanguageKey(lang);
    if (!key) return false;
    if (key === 'english' || key === 'hindi') return true;
    if (key.startsWith('en') || key.startsWith('hi')) return true;
    if (key.includes('english') || key.includes('hindi')) return true;
    return false;
  };
  const normalizePostVadWait = (value) => {
    const source = (value && typeof value === 'object') ? value : {};
    const parsed = Number(source.secs);
    return {
      enabled: normalizeYesNo(source.enabled, POST_VAD_WAIT_DEFAULTS.enabled),
      secs: Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : POST_VAD_WAIT_DEFAULTS.secs
    };
  };
  const normalizeSmartTurnDetection = (value, lang) => {
    const source = (value && typeof value === 'object') ? value : {};
    const supported = isSmartTurnSupportedForLanguage(lang);
    return {
      enabled: supported ? normalizeYesNo(source.enabled, SMART_TURN_DEFAULTS.enabled) : 'No',
      smart_turn_timeout: normalizeSmartTurnTimeout(source.smart_turn_timeout, SMART_TURN_DEFAULTS.smart_turn_timeout)
    };
  };
  const normalizeLlmTurnDetection = (value, backendDefaults) => {
    const source = (value && typeof value === 'object') ? value : {};
    const bd = (backendDefaults && typeof backendDefaults === 'object') ? backendDefaults : {};
    const parseTimeout = (v, fallback) => {
      const parsed = Number(v);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
    };
    return {
      enabled: normalizeYesNo(source.enabled, normalizeYesNo(bd.enabled, LLM_TURN_DETECTION_DEFAULTS.enabled)),
      instruction: typeof source.instruction === 'string' ? source.instruction : (bd.instruction || LLM_TURN_DETECTION_DEFAULTS.instruction),
      incomplete_short_timeout: parseTimeout(source.incomplete_short_timeout, parseTimeout(bd.incomplete_short_timeout, LLM_TURN_DETECTION_DEFAULTS.incomplete_short_timeout)),
      short_timeout_restart_instruction: typeof source.short_timeout_restart_instruction === 'string' ? source.short_timeout_restart_instruction : (bd.short_timeout_restart_instruction || LLM_TURN_DETECTION_DEFAULTS.short_timeout_restart_instruction),
      incomplete_long_timeout: parseTimeout(source.incomplete_long_timeout, parseTimeout(bd.incomplete_long_timeout, LLM_TURN_DETECTION_DEFAULTS.incomplete_long_timeout)),
      long_timeout_restart_instruction: typeof source.long_timeout_restart_instruction === 'string' ? source.long_timeout_restart_instruction : (bd.long_timeout_restart_instruction || LLM_TURN_DETECTION_DEFAULTS.long_timeout_restart_instruction)
    };
  };
  const getAvailableLanguageKeys = (agentObj) => {
    const raw = [
      agentObj?.language,
      ...(Array.isArray(agentObj?.secondary_languages) ? agentObj.secondary_languages : [])
    ];
    return Array.from(new Set(raw.map(normalizeLanguageKey).filter(Boolean)));
  };
  const normalizeAgentLanguageWiseMessages = agentObj => {
    const working = normalizeAgentMessageRules(agentObj || {});
    return {
      ...working,
      chat: normalizeChat(working.chat),
      knowledge_base: {
        ...working.knowledge_base,
        cancel_on_interruption: normalizeBooleanFlag(working.knowledge_base?.cancel_on_interruption, false),
      },
      custom_tools: working.custom_tools.map(tool => ({
        ...tool, cancel_on_interruption: normalizeBooleanFlag(tool?.cancel_on_interruption, false),
      })),
    };
  };

  // Helpers
  function getCookie(name) {
    function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
    var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
    return match ? match[1] : null;
  }

  // Handle dynamic variable addition
  const handleAddDynamicVariable = (variable) => {
    setDynamicVariables(prev => {
      const exists = prev.find(v => v.name === variable.name);
      if (exists) {
        return prev; // Don't add duplicates
      }
      const newVariables = [...prev, variable];
      console.log('Added dynamic variable:', variable);
      return newVariables;
    });
  };

  // Init
  useEffect(() => {
    const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
    setEmail(user?.email || null);
    setRole(user?.role || 'admin');
    setClient_id(clientID);
    setService_id(serviceID);
  }, [clientID, serviceID]);

  const [initialAgentSetup, setInitialAgentSetup] = useState(null);

  const getKnowledgeBaseData = async () => {
    try {
      let res = await server.getKnowledgeBaseData(client_id, email, 'voice-agents', 'agents');
      if(res.status === 200){
        let resp = await res.json();
        const documents = resp.data.filter(item => item.resource_type !== 'doctor_schedule');
        setKnowledgeBaseNames(documents.map(item => item.display_name));
        setKnowledgeBaseMap(documents)
      }
    } catch (error) {
      console.log(error);
    }
    finally {
    }
  }

  // Normalize knowledge_base to unified structure: { knowledge_base_id: [{ id, name }], ... }
  const normalizeKnowledgeBase = (kb) => {
    const src = kb || {};
    const idArray = src?.knowledge_base_id;
    const nameArray = src?.knowledge_base_name;
    const mapList = Array.isArray(knowledgeBaseMap) ? knowledgeBaseMap : [];
    const idToName = new Map(mapList.map(item => [String(item.knowledge_base_id), item.display_name]));
    const nameToId = new Map(mapList.map(item => [item.display_name, String(item.knowledge_base_id)]));
    const coerceId = (val) => (val === undefined || val === null) ? '' : String(val);
    let combined = [];
    if (Array.isArray(idArray) && idArray.length > 0) {
      if (idArray.every(v => v && typeof v === 'object')) {
        combined = idArray.map(v => {
          const vid = coerceId(v.id ?? v.knowledge_base_id);
          const vname = v.name ?? v.display_name ?? (vid ? idToName.get(vid) : '');
          return vid ? { id: vid, name: vname || '' } : null;
        }).filter(Boolean);
      } else {
        combined = idArray.map((id, idx) => {
          const vid = coerceId(id);
          const vname = Array.isArray(nameArray) ? (nameArray[idx] || idToName.get(vid) || '') : (idToName.get(vid) || '');
          return vid ? { id: vid, name: vname || '' } : null;
        }).filter(Boolean);
      }
    } else if (Array.isArray(nameArray) && nameArray.length > 0) {
      combined = nameArray.map(n => {
        const vid = nameToId.get(n) || '';
        return vid ? { id: vid, name: n } : null;
      }).filter(Boolean);
    } else {
      combined = [];
    }
    const { knowledge_base_name, ...rest } = src || {};
    return { ...rest, knowledge_base_id: combined };
  };

  // Derive selected KB names for the multi-select dropdown
  const getSelectedKnowledgeBaseNames = () => {
    const list = selectedAgent?.knowledge_base?.knowledge_base_id || [];
    if (!Array.isArray(list)) return [];
    return list
      .map(item => (item && typeof item === 'object') ? (item.name ?? item.display_name ?? '') : '')
      .filter(Boolean);
  };

  // Handle KB name selection change (maps names -> [{id,name}])
  const handleKnowledgeBaseChange = (e) => {
    const selectedNames = Array.isArray(e?.target?.value) ? e.target.value : [];
    const kbArray = selectedNames.map(n => {
      const found = (knowledgeBaseMap || []).find(x => x.display_name === n);
      return found ? { id: String(found.knowledge_base_id), name: found.display_name } : null;
    }).filter(Boolean);
    updateSelectedAgent({
      ...selectedAgent,
      knowledge_base: {
        ...(selectedAgent?.knowledge_base || {}),
        knowledge_base_id: kbArray
      }
    });
  };

  // Decide create vs edit from URL
  useEffect(() => {
    if (!client_id || !email) return;
    const params = new URLSearchParams(location.search);
    const isCreate = params.get('action') === 'create';
    setIsCreating(isCreate);
    setLoading(true);

    const bootstrap = async () => {
      try {
        const fetchedConfig = await getAllowedConfig();
        await getPhoneNumbersList();
        await getServiceOptions();
        await getActiveAgentOptions();
        await getKnowledgeBaseData();

        if (isCreate) {
          const name = params.get('name') ? decodeURIComponent(params.get('name')) : '';
          const initialAgent = buildInitialAgent(agentId, name, fetchedConfig);
          setInitialAgentSetup(initialAgent);
          setSelectedAgent(deepClone(initialAgent));
          setBaselineAgent(deepClone(initialAgent));
          setLiveAgentData(deepClone(initialAgent));
          const initialAnalyticsConfig = analyticsConfigFromObject(null, fetchedConfig);
          setInbuiltAnalyticsConfig(initialAnalyticsConfig);
          setBaselineInbuiltAnalyticsConfig(deepClone(initialAnalyticsConfig));
          const initialFutureSchedulingConfig = normalizeFutureSchedulingConfig(initialAgent?.future_scheduling);
          setFutureSchedulingConfig(initialFutureSchedulingConfig);
          setBaselineFutureSchedulingConfig(deepClone(initialFutureSchedulingConfig));
        } else {
          await getAgentDetails(agentId, fetchedConfig);
        }
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, [client_id, email, agentId, location.search]);
  const handleVariableConfigUpdate = (variableName, configType, value) => {
    console.log(`Updating variable config: ${variableName}, ${configType}, ${value}`);
    if(configType === 'delete'){
      setVariableConfigs(prevConfigs => {
        const { [variableName]: _, ...rest } = prevConfigs;
        return rest;
      });
      setDynamicVariables(prevVariables => {
        return prevVariables.filter(variable => variable.name !== variableName);
      });
      return;
    }
    setVariableConfigs(prevConfigs => {
      const newConfigs = {
        ...prevConfigs,
        [variableName]: {
          ...prevConfigs[variableName],
          [configType]: value
        }
      };
      console.log('Updated variableConfigs:', newConfigs);
      return newConfigs;
    });
  };
  const convertCustomVariablesToData = (customVariables) => {
    const dynamicVarsArray = [];
    const configsObject = {};

    if (customVariables && typeof customVariables === 'object') {
      Object.entries(customVariables).forEach(([varName, varData]) => {
        // Create dynamic variable object
        dynamicVarsArray.push({
          name: varName,
          description: varData.description || '',
          category: 'Custom',
          variable_type: varData.type || 'string',
          created: new Date().toISOString()
        });

        // Create config object for UI
        configsObject[varName] = {
          required: varData.required === "yes",
          defaultValue: varData.default_value || ""
        };
      });
    }

    return { dynamicVariables: dynamicVarsArray, variableConfigs: configsObject };
  };
  const convertDataToCustomVariables = (variables = dynamicVariables, configs = variableConfigs) => {
    const customVariables = {};

    // Only process dynamic variables, not system variables
    variables.forEach(variable => {
      const config = configs[variable.name] || { required: false, defaultValue: "" };
      customVariables[variable.name] = {
        required: config.required ? "yes" : "no",
        description: variable.description || "",
        default_value: config.defaultValue || "",
        type: variable.variable_type || "string"
      };
    });

    console.log('Converting to custom_variables:', customVariables);
    return customVariables;
  };

  // Build a new agent template
  const buildInitialAgent = (id, name, allowedConfig = configData) => ({
    agent_id: id,
    agent_name: name || '',
    description: '',
    company: '',
    status: 'inactive',
    phonenumber: '',
    first_message: '',
    system_prompt: '',
    variable_configuration: { url: '', header: '', method: 'GET' },
    secondary_languages: [],
    llm: { provider: '', model: '' },
    voice: { provider: '', model: '', voice: '', gender: 'female', tts_cache: 1 },
    background_effects: { noise: 'No', noise_volume: 50 },
    noise_cancellation: normalizeNoiseCancellation(undefined, allowedConfig),
    transcription: { provider: '', model: '', reasoning_effort: 'Standard', keywords: '' },
    vad_params: { ...VAD_DEFAULTS },
    post_vad_wait_for_user: { ...POST_VAD_WAIT_DEFAULTS },
    conversation_controls: normalizeConversationControls(),
    smart_turn_detection: { ...SMART_TURN_DEFAULTS },
    llm_turn_detection: { ...LLM_TURN_DETECTION_DEFAULTS },
    interruptions: { minimum_number_of_words: 3, minimum_speech_secs: 0.3, interruption_allowed_for_agent_first_message: 'No' },
    conversation_hangup: { hangup_based_on_intent_instruction: '', hangup_message: '', hangup_message_rules: [] },
    conversation_termination: { maximum_conversation_duration: '300', termination_message: '' },
    conversation_turn_timeout: { turn_timeout_seconds: '10', turn_timeout_message: '', turn_timeout_message_rules: [], max_tries_for_conversation_turn_timeout: 3, last_message: '', last_message_rules: [] },
    call_transfer: { enabled: 'No', accept_start_message_from_llm: 'No', transfer_type: 'Cold', instructions: '', start_message: '', start_message_rules: [], delay_till_start_message_spoken: 'No', enable_webhook: 'No', webhook_url: '', headers: '' },
    post_call_analytics_enabled: 'No',
    post_call_analytics_service_id: '',
    post_call_analytics_transcription_mode: allowedConfig?.post_call_analytics_transcription_mode?.[0] || '',
    widget: { enabled: 'No', widget_namespace: '', widget_size: '', widget_avatar_url: '' },
    voice_mail: { voice_mail_action: 'Hangup', voice_mail_leave_message: '' },
    dtmf_enabled: 'No',
    dtmf_idle_timeout_seconds: 2,
    custom_variables: {},
    knowledge_base: { knowledge_base_id: [], start_message: '', start_message_rules: [], delay_till_start_message_spoken: 'No', cancel_on_interruption: false },
    custom_tools: [],
    future_scheduling: createDefaultFutureSchedulingConfig(),
    chat: normalizeChat(),
    pipeline_type: 'stt_llm_tts',
    voice_to_voice: { provider: '', model: '', voice: '', gender: 'female', speed: 1.0 }
  });
  const [inbuiltAnalyticsConfig, setInbuiltAnalyticsConfig] = useState({ run_for_not_connected: false, run_for_non_meaningful: false, subparameters: [], parameters: [] });
  const [baselineInbuiltAnalyticsConfig, setBaselineInbuiltAnalyticsConfig] = useState({ run_for_not_connected: false, run_for_non_meaningful: false, subparameters: [], parameters: [] });
  const [futureSchedulingConfig, setFutureSchedulingConfig] = useState(createDefaultFutureSchedulingConfig());
  const [baselineFutureSchedulingConfig, setBaselineFutureSchedulingConfig] = useState(createDefaultFutureSchedulingConfig());

  // Deep clone function for objects
  const deepClone = (obj) => {
    if (obj === null || typeof obj !== "object") return obj;
    if (obj instanceof Date) return new Date(obj.getTime());
    if (obj instanceof Array) return obj.map(item => deepClone(item));
    if (typeof obj === "object") {
      const copy = {};
      Object.keys(obj).forEach(key => copy[key] = deepClone(obj[key]));
      return copy;
    }
  };

  // Compare two agent objects to detect changes
  const hasAgentChanged = (current, baseline) => {
    if (!current || !baseline) return false;
    return JSON.stringify(current) !== JSON.stringify(baseline);
  };

  // Include separately edited variables in the saved-draft change check.
  useEffect(() => {
    if (selectedAgent && baselineAgent) {
      const agentChanged = hasAgentChanged(selectedAgent, baselineAgent);
      const analyticsChanged = JSON.stringify(inbuiltAnalyticsConfig || {}) !== JSON.stringify(baselineInbuiltAnalyticsConfig || {});
      const futureSchedulingChanged = JSON.stringify(futureSchedulingConfig || {}) !== JSON.stringify(baselineFutureSchedulingConfig || {});
      const baselineVariables = convertCustomVariablesToData(baselineAgent.custom_variables);
      const variablesChanged = hasAgentChanged(
        convertDataToCustomVariables(),
        convertDataToCustomVariables(baselineVariables.dynamicVariables, baselineVariables.variableConfigs),
      );
      const changed = agentChanged || analyticsChanged || futureSchedulingChanged || variablesChanged;
      setChangesOngoing(changed);
    }
  }, [selectedAgent, baselineAgent, inbuiltAnalyticsConfig, baselineInbuiltAnalyticsConfig, futureSchedulingConfig, baselineFutureSchedulingConfig, dynamicVariables, variableConfigs]);

  const updateSelectedAgent = (newAgent) => {
    setConfigurationErrors({});
    // Convert current variable configs to custom_variables format before updating
    const customVariables = convertDataToCustomVariables();
    const agentWithCustomVars = normalizeAgentLanguageWiseMessages({
      ...newAgent,
      llm: normalizeSelectedLlm(newAgent?.llm),
      custom_variables: customVariables,
      vad_params: normalizeVadParams(newAgent?.vad_params),
      transcription_controls: newAgent?.transcription_controls,
      post_vad_wait_for_user: normalizePostVadWait(newAgent?.post_vad_wait_for_user),
      conversation_controls: normalizeConversationControls(newAgent?.conversation_controls),
      smart_turn_detection: normalizeSmartTurnDetection(newAgent?.smart_turn_detection, newAgent?.language),
      llm_turn_detection: normalizeLlmTurnDetection(newAgent?.llm_turn_detection, configData?.llm_turn_detection),
      noise_cancellation: normalizeNoiseCancellation(newAgent?.noise_cancellation, configData)
    });
    console.log('Updating selected agent with custom_variables:', agentWithCustomVars);
    setSelectedAgent(agentWithCustomVars);
  };

  // Fetch endpoints
  const getAllowedConfig = async () => {
    try {
      let res = await server.get_voice_agent_allowed_config();
      if (res.status === 200) {
        let resp = await res.json();
        const newConfig = resp.data || {};
        setConfigData(newConfig);
        const llmProviders = Object.keys(newConfig.llm || {});
        const voiceProviders = Object.keys(newConfig.voice || {});
        const transcriptionProviders = Object.keys(newConfig.transcription || {});
        setLlmProviderOptions(llmProviders);
        setVoiceProviderOptions(voiceProviders);
        setTranscriptionProviderOptions(transcriptionProviders);
        const v2vProviders = Object.keys(newConfig.voice_to_voice || {});
        setV2vProviderOptions(v2vProviders);
        if (v2vProviders.length > 0) {
          updateV2vModelOptions(v2vProviders[0], 'female', newConfig);
        }
        const languages = Array.isArray(newConfig.languages)
          ? newConfig.languages
          : Array.isArray(newConfig.language)
            ? newConfig.language
            : Array.isArray(newConfig?.voice?.languages)
              ? newConfig.voice.languages
              : [];
        setLanguageOptions(languages);
        const secondaryLangs = Array.isArray(newConfig.secondary_languages)
          ? newConfig.secondary_languages
          : languages;
        setSecondaryLanguageOptions(secondaryLangs);
        const systemVars = [];
        if (newConfig.system_variables) {
          Object.entries(newConfig.system_variables).forEach(([key, value]) => {
            systemVars.push({
              name: key,
              variable_type: value.type || 'string',
              description: value.description || '',
              category: 'System'
            });
          });
        }
        setSystemVariables(systemVars);
        return newConfig;
      }
    } catch (error) {
      console.error('Error fetching allowed config:', error);
    }
    return {};
  };

  const getAgentDetails = async (id, allowedConfig) => {
    try {
      let res = await server.getAgentDetails(client_id, '', email, 'voice-agents', 'agents', id);
      if (res.status === 200) {
        let resp = await res.json();
        const agentData = resp.data;
        setLiveMetadata(resp.metadata || null);
        const agentDataNormalizedBase = { ...agentData, custom_tools: Array.isArray(agentData?.custom_tools) ? agentData.custom_tools : [] };
        const effectiveConfig = allowedConfig || configData;
        const agentDataNormalized = {
          ...agentDataNormalizedBase,
          llm: normalizeSelectedLlm(agentDataNormalizedBase?.llm, effectiveConfig),
          knowledge_base: normalizeKnowledgeBase(agentDataNormalizedBase?.knowledge_base),
          vad_params: normalizeVadParams(agentDataNormalizedBase?.vad_params),
          transcription_controls: agentDataNormalizedBase?.transcription_controls
            ? normalizeTranscriptionControls(agentDataNormalizedBase.transcription_controls)
            : undefined,
          post_vad_wait_for_user: normalizePostVadWait(agentDataNormalizedBase?.post_vad_wait_for_user),
          conversation_controls: normalizeConversationControls(
            agentDataNormalizedBase?.conversation_controls,
          ),
          smart_turn_detection: normalizeSmartTurnDetection(agentDataNormalizedBase?.smart_turn_detection, agentDataNormalizedBase?.language),
          llm_turn_detection: normalizeLlmTurnDetection(agentDataNormalizedBase?.llm_turn_detection, effectiveConfig?.llm_turn_detection),
          noise_cancellation: normalizeNoiseCancellation(agentDataNormalizedBase?.noise_cancellation, effectiveConfig),
          pipeline_type: agentDataNormalizedBase?.pipeline_type || 'stt_llm_tts',
          voice_to_voice: {
            provider: '',
            model: '',
            voice: '',
            gender: 'female',
            speed: 1.0,
            ...(agentDataNormalizedBase?.voice_to_voice || {})
          }
        };
        const normalizedAgent = normalizeAgentLanguageWiseMessages(agentDataNormalized);
        setSelectedAgent(normalizedAgent);
        const { dynamicVariables: dynVars, variableConfigs: configs } = convertCustomVariablesToData(normalizedAgent?.custom_variables);
        setDynamicVariables(dynVars);
        setVariableConfigs(configs);
        setLiveAgentData(deepClone(normalizedAgent));
        setBaselineAgent(deepClone(normalizedAgent));
        setDeployedStatus(agentData?.is_active);
        const fetchedAnalyticsConfig = analyticsConfigFromObject(agentData?.inbuilt_analytics, allowedConfig || configData);
        setInbuiltAnalyticsConfig(fetchedAnalyticsConfig);
        setBaselineInbuiltAnalyticsConfig(deepClone(fetchedAnalyticsConfig));
        const fetchedFutureSchedulingConfig = normalizeFutureSchedulingConfig(agentData?.future_scheduling);
        setFutureSchedulingConfig(fetchedFutureSchedulingConfig);
        setBaselineFutureSchedulingConfig(deepClone(fetchedFutureSchedulingConfig));
        if(agentData?.version){
          setCurrent_version(agentData?.version)
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getPhoneNumbersList = async () => {
    try {
      let res = await server.get_voice_agent_phone_number_list(client_id, '', email, 'voice-agents', 'agents', 1);
      if (res.status === 200) {
        let resp = await res.json();
        let numbers = [];
        for (let i = 0; i < resp.phone_numbers.length; i++) {
          numbers.push(resp.phone_numbers[i].phone_number);
        }
        setPhoneNumbers(numbers);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getServiceOptions = async () => {
    try {
      let res = await server.getProjectsList(client_id, '', email, 'voice-agents', 'agents', 'superadmin');
      if (res.status === 200) {
        let resp = await res.json();
        setServiceOptions(resp.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getActiveAgentOptions = async () => {
    try {
      const res = await server.getAgentsList(client_id, '', email, 'voice-agents', 'agents');
      if (res.status !== 200) return;
      const resp = await res.json();
      const options = (Array.isArray(resp?.data) ? resp.data : [])
        .filter((agent) => agent?.is_archived !== true)
        .map((agent) => ({
          id: agent?.agent_id,
          name: agent?.agent_name || agent?.agent_id,
          is_active: agent?.is_active !== false,
          is_archived: agent?.is_archived === true,
        }))
        .filter((agent) => agent.id && agent.is_active);
      setActiveAgentOptions(options);
    } catch (error) {
      console.error('Error fetching active voice-agent options:', error);
      setActiveAgentOptions([]);
    }
  };

  // Option population helpers
  const updateModelOptions = (provider, type, cfg) => {
    const effectiveConfig = cfg || configData;
    if (!provider || !effectiveConfig?.[type] || !effectiveConfig?.[type]?.[provider]) {
      setLlmModelOptions([]);
      return;
    }
    const models = Object.keys(effectiveConfig[type][provider] || {});
    setLlmModelOptions(models);
  };

  const updateTranscriptionModelOptions = (provider, cfg) => {
    const effectiveConfig = cfg || configData;
    if (!provider || !effectiveConfig?.transcription || !effectiveConfig?.transcription?.[provider]) {
      setTranscriptionModelOptions([]);
      return;
    }
    const models = Object.keys(effectiveConfig.transcription[provider] || {});
    setTranscriptionModelOptions(models);
  };

  const updateVoiceModelOptions = (provider, cfg) => {
    const effectiveConfig = cfg || configData;
    if (!provider || !effectiveConfig?.voice || !effectiveConfig?.voice?.[provider]) {
      setVoiceModelOptions([]);
      return;
    }
    const models = Object.keys(effectiveConfig.voice[provider] || {});
    setVoiceModelOptions(models);
    if (models.length > 0) {
      updateVoiceOptions(provider, models[0], selectedVoiceGender, effectiveConfig);
    }
  };

  const updateV2vModelOptions = (provider, gender, cfg) => {
    const effectiveConfig = cfg || configData;
    if (!provider || !effectiveConfig?.voice_to_voice?.[provider]) {
      setV2vModelOptions([]);
      setV2vVoiceOptions([]);
      return;
    }
    const models = Object.keys(effectiveConfig.voice_to_voice[provider] || {});
    setV2vModelOptions(models);
    if (models.length > 0) {
      updateV2vVoiceOptions(provider, models[0], gender || selectedV2vGender, effectiveConfig);
    }
  };

  const updateV2vVoiceOptions = (provider, model, gender, cfg) => {
    const effectiveConfig = cfg || configData;
    const voices = effectiveConfig?.voice_to_voice?.[provider]?.[model]?.voices?.[gender] || [];
    setV2vVoiceOptions(voices.map(v => (typeof v === 'object' ? v.name : v)));
  };

  // Helper function to get voice ID from voice name
  const getVoiceIdFromName = (provider, model, gender, voiceName) => {
    if (!provider || !model || !gender || !voiceName) return voiceName;

    const voices = configData?.voice?.[provider]?.[model]?.voices?.[gender] || [];
    // Dropdown displays voice.name, but we store voice.id in config.
    // If the value isn't found (or dropdown is showing an id), fall back to returning the raw value.
    const voiceObj = voices.find(
      (voice) =>
        typeof voice === 'object' &&
        ((voice.name && voice.name === voiceName) || (voice.id && voice.id === voiceName))
    );

    return voiceObj ? voiceObj.id : voiceName;
  };

  // Helper function to get voice name from voice ID
  const getVoiceNameFromId = (provider, model, gender, voiceId) => {
    if (!provider || !model || !gender || !voiceId) return voiceId;

    const voices = configData?.voice?.[provider]?.[model]?.voices?.[gender] || [];
    const voiceObj = voices.find(
      (voice) => typeof voice === 'object' && voice.id === voiceId
    );

    // If we have a name, show it in the UI. Otherwise fall back to id.
    return voiceObj ? (voiceObj.name || voiceObj.id || voiceId) : voiceId;
  };

  // Helper function to get the first voice ID for a given provider/model/gender
  const getFirstVoiceId = (provider, model, gender, cfg) => {
    const effectiveConfig = cfg || configData;
    const voices = effectiveConfig?.voice?.[provider]?.[model]?.voices?.[gender] || [];
    if (voices.length === 0) return '';

    const firstVoice = voices[0];
    return typeof firstVoice === 'object' && firstVoice.id ? firstVoice.id : firstVoice;
  };

  const updateVoiceOptions = (provider, model, gender, cfg) => {
    const effectiveConfig = cfg || configData;
    if (!provider || !model || !gender ||
        !effectiveConfig?.voice ||
        !effectiveConfig?.voice?.[provider] ||
        !effectiveConfig?.voice?.[provider]?.[model] ||
        !effectiveConfig?.voice?.[provider]?.[model]?.voices ||
        !effectiveConfig?.voice?.[provider]?.[model]?.voices?.[gender]) {
      setVoiceOptions([]);
      return;
    }
    const voices = effectiveConfig.voice[provider][model].voices[gender] || [];
    // Convert voice objects to name strings for dropdown display (fallback to id)
    const voiceNames = voices
      .map((voice) => {
        if (typeof voice === 'object') return voice.name || voice.id || '';
        return voice || '';
      })
      .filter(Boolean);
    // De-dupe while preserving order
    const uniqueVoiceNames = Array.from(new Set(voiceNames));
    setVoiceOptions(uniqueVoiceNames);
  };

  // Keep dropdowns in sync with state and config
  useEffect(() => {
    if (!selectedAgent) return;
    if (selectedAgent.llm?.provider) {
      updateModelOptions(selectedAgent.llm.provider, 'llm');
    } else {
      setLlmModelOptions([]);
    }
    if (selectedAgent.voice?.provider) {
      updateVoiceModelOptions(selectedAgent.voice.provider);
      if (selectedAgent.voice?.model) {
        const gender = selectedAgent.voice?.gender || selectedVoiceGender;
        updateVoiceOptions(selectedAgent.voice.provider, selectedAgent.voice.model, gender);
      } else {
        setVoiceOptions([]);
      }
    } else {
      setVoiceModelOptions([]);
      setVoiceOptions([]);
    }
    if (selectedAgent.transcription?.provider) {
      updateTranscriptionModelOptions(selectedAgent.transcription.provider);
    } else {
      setTranscriptionModelOptions([]);
    }
  }, [configData, selectedAgent, selectedVoiceGender]);

  // Ensure voice.speed default/clamp based on config
  useEffect(() => {
    if (!selectedAgent?.voice?.provider || !selectedAgent?.voice?.model) return;
    const provider = selectedAgent.voice.provider;
    const model = selectedAgent.voice.model;
    const speedCfg = configData?.voice?.[provider]?.[model]?.speed;
    if (speedCfg?.available === 1) {
      const min = typeof speedCfg.min_value === 'number' ? speedCfg.min_value : 0.5;
      const max = typeof speedCfg.max_value === 'number' ? speedCfg.max_value : 2;
      const def = typeof speedCfg.default_value === 'number' ? speedCfg.default_value : 1;
      let currentSpeed = selectedAgent?.voice?.speed;
      if (typeof currentSpeed !== 'number') currentSpeed = def;
      if (currentSpeed < min) currentSpeed = min;
      if (currentSpeed > max) currentSpeed = max;
      if (selectedAgent?.voice?.speed !== currentSpeed) {
        updateSelectedAgent({ ...selectedAgent, voice: { ...selectedAgent.voice, speed: currentSpeed } });
      }
    } else {
      if (selectedAgent?.voice?.speed != null) {
        const { speed, ...rest } = selectedAgent.voice || {};
        updateSelectedAgent({ ...selectedAgent, voice: { ...rest } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configData, selectedAgent?.voice?.provider, selectedAgent?.voice?.model]);

  // Auto defaults when empty
  useEffect(() => {
    if (!selectedAgent) return;
    if (!selectedAgent.language && languageOptions && languageOptions.length > 0) {
      updateSelectedAgent({ ...selectedAgent, language: languageOptions[0] });
    }
  }, [selectedAgent, languageOptions]);

  useEffect(() => {
    if (!selectedAgent) return;
    const noiseOptions = (configData?.background_effects?.noise) || [];
    const currentNoise = selectedAgent?.background_effects?.noise;
    if ((currentNoise === '' || currentNoise == null) && noiseOptions.length > 0) {
      updateSelectedAgent({
        ...selectedAgent,
        background_effects: {
          ...(selectedAgent?.background_effects || {}),
          noise: noiseOptions[0]
        }
      });
    }
  }, [selectedAgent, configData]);

  // Test workflow
  const handleTestWorkflowChange = (e) => {
    if (isCallActive || callStartingRef.current || isChatTestActive || submitLoading || showOutboundModal) return;
    const choice = e.target.value;
    if (choice !== 'Test Chat') {
      setTestChatTarget(null);
      setTestChatOpen(false);
    }
    if (choice === 'Test in Browser') {
      startCall();
    } else if (choice === 'Test Outbound') {
      setShowOutboundModal(true);
    } else if (choice === 'Test Chat' && canTestChat(selectedAgent, configData)) {
      if (!testChatTarget) setTestChatTarget(getCurrentChatTestTarget());
      setTestChatOpen(true);
    }
    setTestWorkflowOption('');
  };

  const getPhoneNumber = (countryCode, phoneNumber) => {
    let map = {
      '+91 (INDIA)': '+91'
    }
    if(map[countryCode]){
      return map[countryCode] + phoneNumber;
    }
    return phoneNumber;
  }


  const handleSubmitOutbound = async () => {
    try {
      if (!outboundPhoneNumber || submitLoading || isCallActive || callStartingRef.current || isChatTestActive) {
        return;
      }
      if(outboundPhoneNumber.length !== 10){
        showToast('Phone number must be 10 digits', 'error');
        return;
      }
      setSubmitLoading(true);
      let phoneNumber = getPhoneNumber(countryCode, outboundPhoneNumber);
      const res = await server.test_voice_agent_outbound_call(client_id, selectedAgent.agent_id, phoneNumber, selectedAgent?.phonenumber, email, 'voice-agents', undefined, selectedStatus);
      if (res.status === 200) { let resp = await res.json();; showToast(resp.message, 'success'); }
    } catch (_) {
      showToast("Failed to initiate outbound call", 'error');
    }
    setShowOutboundModal(false);
    setOutboundPhoneNumber('');
    setSubmitLoading(false);
  };

  const buildPersistableAgent = (agent, analyticsConfig, useCurrentVariables = false, schedulingConfig = undefined) => {
    if (!agent) return null;
    const vc = agent?.variable_configuration || {};
    const normHeader = String(vc.header || '')
      .split(';')
      .map(s => s.trim())
      .filter(Boolean)
      .map(p => p.replace(/\s*:\s*/g, ':'))
      .join(';');
    const agentToPersist = {
      ...agent,
      dtmf_enabled: agent?.dtmf_enabled ?? 'No',
      dtmf_idle_timeout_seconds: agent?.dtmf_idle_timeout_seconds ?? 2,
      llm: normalizeSelectedLlm(agent?.llm),
      variable_configuration: { url: String(vc.url || '').trim(), header: normHeader, method: (vc.method === 'POST' ? 'POST' : 'GET') },
      custom_variables: useCurrentVariables ? convertDataToCustomVariables() : (agent?.custom_variables || {}),
      vad_params: normalizeVadParams(agent?.vad_params),
      transcription_controls: (
        supportsTranscriptionControls(agent) || agent?.transcription_controls
      ) ? normalizeTranscriptionControls(agent?.transcription_controls) : undefined,
      post_vad_wait_for_user: normalizePostVadWait(agent?.post_vad_wait_for_user),
      conversation_controls: normalizeConversationControls(agent?.conversation_controls),
      smart_turn_detection: normalizeSmartTurnDetection(agent?.smart_turn_detection, agent?.language),
      llm_turn_detection: normalizeLlmTurnDetection(agent?.llm_turn_detection, configData?.llm_turn_detection),
      noise_cancellation: normalizeNoiseCancellation(agent?.noise_cancellation, configData),
      knowledge_base: normalizeKnowledgeBase(agent?.knowledge_base),
      post_call_analytics_transcription_mode: agent?.post_call_analytics_transcription_mode || configData?.post_call_analytics_transcription_mode?.[0] || '',
      inbuilt_analytics: analyticsObjectFromConfig(analyticsConfig),
      future_scheduling: normalizeFutureSchedulingConfig(schedulingConfig ?? agent?.future_scheduling)
    };
    return normalizeAgentLanguageWiseMessages(agentToPersist);
  };

  const getDraftAnalyticsConfig = () => (
    draftAgentData ? analyticsConfigFromObject(draftAgentData?.inbuilt_analytics, configData) : inbuiltAnalyticsConfig
  );

  const getDeployableAgent = () => {
    if (selectedStatus === 'Draft Version' && !changesOngoing && draftAgentData) {
      return buildPersistableAgent(draftAgentData, getDraftAnalyticsConfig());
    }
    return buildPersistableAgent(selectedAgent, inbuiltAnalyticsConfig, true, futureSchedulingConfig);
  };

  const getAuthoritativeAgentId = () => agentId || selectedAgent?.agent_id || null;

  const validateFutureSchedulingForSave = (schedulingConfig, analyticsConfig = inbuiltAnalyticsConfig) => {
    const errors = validateFutureSchedulingConfig(
      schedulingConfig,
      getFutureSchedulingAnalyticsFields(analyticsConfig),
      activeAgentOptions,
    );
    if (errors.length > 0) {
      showToast(`Future Scheduling: ${errors[0]}`, 'error');
      return false;
    }
    return true;
  };
  const getCurrentChatTestTarget = () => buildChatTestTarget({
    agent: { ...buildPersistableAgent(selectedAgent, inbuiltAnalyticsConfig, true, futureSchedulingConfig), agent_id: getAuthoritativeAgentId() },
    version: current_version,
    useDraft: selectedStatus === 'Draft Version' || changesOngoing,
  });

  const validateVoiceAgentConfiguration = async (configuration) => {
    const reservedNames = new Set(Object.keys(configData?.inbuilt_analytics_system_parameters || {}));
    const analytics = configuration.inbuilt_analytics || {};
    for (const section of ['subparameters', 'parameters']) {
      const collision = Object.entries(analytics[section] || {}).find(([name, item]) => (
        (name.startsWith('prev_') || reservedNames.has(name))
        && !(section === 'subparameters' && [1, true, '1'].includes(item?.system))
      ));
      if (collision) {
        setAgentDetailsTab('inbuilt_analytics');
        showToast(`Analytics name ${collision[0]} is reserved. Rename this user-defined field before saving.`, 'error');
        return false;
      }
    }
    const chatErrors = validateChat(configuration, configData);
    const feedbackError = validateToolFeedback(configuration.knowledge_base);
    if (feedbackError) chatErrors['knowledge_base.start_delay_seconds'] = feedbackError;
    const suffixError = validateMessageRules((configuration.system_prompt_suffix_rules || []).map(rule => ({ ...rule, language: rule.language || 'all' })), 30000);
    if (suffixError) chatErrors.system_prompt_suffix_rules = suffixError;
    setConfigurationErrors(chatErrors);
    if (Object.keys(chatErrors).length) {
      setAgentDetailsTab(getConfigurationTab(Object.keys(chatErrors)[0]));
      showToast(Object.values(chatErrors).join(' '), 'error');
      return false;
    }
    try {
      const authoritativeAgentId = getAuthoritativeAgentId();
      const validationResponse = await server.validateVoiceAgentConfiguration({
        client_id,
        service_id: Array.isArray(service_id) ? service_id[0] : (service_id || ''),
        email,
        menu: 'voice-agents',
        submenu: 'agents',
        agent_id: authoritativeAgentId,
        configuration: {
          ...configuration,
          agent_id: authoritativeAgentId,
        },
      });

      if (validationResponse.status !== 200) {
        const errors = getConfigurationErrors(await validationResponse.clone().json().catch(() => ({})));
        setConfigurationErrors(errors);
        if (Object.keys(errors).length) setAgentDetailsTab(getConfigurationTab(Object.keys(errors)[0]));
        showToast(
          await getApiToastMessage(validationResponse, 'Invalid voice agent configuration'),
          'error',
        );
        return false;
      }

      return true;
    } catch (error) {
      console.error('Voice agent configuration validation failed:', error);
      showToast('Unable to validate voice agent configuration', 'error');
      return false;
    }
  };

  const handleDownloadConfig = () => {
    try {
      const configuration = buildPersistableAgent(selectedAgent, inbuiltAnalyticsConfig, true, futureSchedulingConfig);
      if (!configuration) {
        showToast('No voice agent configuration to download', 'error');
        return;
      }

      const exportedConfiguration = {
        ...configuration,
        _export_metadata: {
          agent_id: configuration.agent_id,
          source: selectedStatus === 'Draft Version' ? 'draft' : 'live',
          version: configuration.version ?? null,
          exported_at: new Date().toISOString(),
        },
      };
      const blob = new Blob([JSON.stringify(exportedConfiguration, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeAgentName = String(configuration.agent_name || 'agent')
        .trim()
        .replace(/[^a-z0-9_-]+/gi, '_')
        .toLowerCase();
      link.href = url;
      link.download = `voice_agent_${safeAgentName}_config_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast('Configuration downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error downloading voice agent configuration:', error);
      showToast('Error downloading configuration', 'error');
    }
  };

  const handleUploadConfig = (event) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (loadEvent) => {
      setLoading(true);
      try {
        const uploadedConfig = JSON.parse(loadEvent.target.result);
        if (!uploadedConfig || typeof uploadedConfig !== 'object' || Array.isArray(uploadedConfig)) {
          throw new Error('Invalid configuration format');
        }

        const existingAgentId = getAuthoritativeAgentId();
        let latestVersion = current_version;
        let latestConfiguration = null;
        if (existingAgentId) {
          const response = await server.getAgentDetails(client_id, '', email, 'voice-agents', 'agents', existingAgentId);
          if (response.status !== 200) {
            showToast('Could not check the current configuration version. Please try uploading again.', 'error');
            return;
          }
          latestConfiguration = await response.json();
          latestVersion = latestConfiguration.data.version;
        }
        const warning = getUploadWarning(uploadedConfig, existingAgentId, latestVersion);
        if (warning) {
          setLoading(false);
          const confirmed = await new Promise(resolve => setUploadConfirmation({ message: warning, resolve }));
          if (!confirmed) return;
          setLoading(true);
        }
        const uploadedConfigWithoutMetadata = Object.fromEntries(
          Object.entries(uploadedConfig).filter(([key]) => !['agent_id', '_export_metadata'].includes(key)),
        );
        const uploadedConfigWithAuthoritativeId = {
          ...uploadedConfigWithoutMetadata,
          agent_id: existingAgentId,
          version: existingAgentId ? latestVersion : uploadedConfigWithoutMetadata.version,
        };

        if (!(await validateVoiceAgentConfiguration(uploadedConfigWithAuthoritativeId))) {
          return;
        }

        const uploadedAnalytics = analyticsConfigFromObject(uploadedConfig.inbuilt_analytics, configData);
        const uploadedFutureScheduling = normalizeFutureSchedulingConfig(uploadedConfigWithAuthoritativeId?.future_scheduling);
        const normalizedConfig = buildPersistableAgent(
          uploadedConfigWithAuthoritativeId,
          uploadedAnalytics,
          false,
          uploadedFutureScheduling,
        );

        const { dynamicVariables: uploadedVariables, variableConfigs: uploadedVariableConfigs } =
          convertCustomVariablesToData(normalizedConfig.custom_variables);
        setDynamicVariables(uploadedVariables);
        setVariableConfigs(uploadedVariableConfigs);
        setInbuiltAnalyticsConfig(uploadedAnalytics);
        setFutureSchedulingConfig(uploadedFutureScheduling);
        setSelectedAgent(normalizedConfig);
        // An intentional import is based on the server version checked above.
        // Later server changes still fail the existing deployment version check.
        if (latestConfiguration) {
          setCurrent_version(latestVersion);
          setLiveAgentData(buildPersistableAgent(
            {
              ...latestConfiguration.data,
              custom_tools: Array.isArray(latestConfiguration.data.custom_tools) ? latestConfiguration.data.custom_tools : [],
            },
            analyticsConfigFromObject(latestConfiguration.data.inbuilt_analytics, configData),
          ));
          setLiveMetadata(latestConfiguration.metadata || null);
        }
        showToast('Configuration uploaded successfully!', 'success');
      } catch (error) {
        console.error('Error uploading voice agent configuration:', error);
        showToast('Error uploading configuration: Invalid JSON format', 'error');
      } finally {
        setLoading(false);
        input.value = '';
      }
    };
    reader.onerror = () => {
      showToast('Error reading configuration file', 'error');
      input.value = '';
    };
    reader.readAsText(file);
  };

  const handleSaveAgent = async (versionDescription) => {
    try {
      if(changesOngoing){
        return;
      }
      setLoading(true);
      const agentToSave = {
        ...getDeployableAgent(),
        agent_id: getAuthoritativeAgentId(),
      };
      if (!deployedStatus && !validateFutureSchedulingForSave(agentToSave?.future_scheduling)) {
        return;
      }
      if (!deployedStatus && !(await validateVoiceAgentConfiguration(agentToSave))) {
        return;
      }
      let obj = {
        content: agentToSave,
        agent_id: agentToSave?.agent_id || selectedAgent?.agent_id || null,
        agent_name: agentToSave?.agent_name,
        phonenumber: agentToSave?.phonenumber,
        deploy_status: !deployedStatus,
        client_id: client_id,
        service_id: '',
        email: email,
        menu: 'voice-agents',
        submenu: 'agents',
        action: isCreating ? 'create' : 'update',
        version_changed: current_version,
        version_description: versionDescription
      };
      let res = await server.saveAgentDetails(obj);
      if (res.status === 200) {
        const resp = await res.json();
        setIsCreating(false);
        setDraftAgentData(null);
        if (!deployedStatus && resp?.data?.version && resp?.data?.version_description) {
          showToast(`Changes deployed successfully as version ${resp.data.version}: ${resp.data.version_description}`, 'success');
        } else {
          showToast(deployedStatus ? 'Changes undeployed successfully' : 'Changes deployed successfully', 'success');
        }
        // After deploy/undeploy, re-fetch latest agent config from backend
        setSelectedStatus('Live Version');
        await getAgentDetails(agentToSave?.agent_id || selectedAgent?.agent_id || agentId);
      }else{
        let resp = await res.json();
        setConfigurationErrors(getConfigurationErrors(resp));
        showToast(resp.message, 'error');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // WebRTC helpers
  const waitForIceGatheringComplete = async (pc, timeoutMs = 2000) => {
    if (pc.iceGatheringState === 'complete') return;
    return new Promise((resolve) => {
      let timeoutId;
      const done = () => { pc.removeEventListener('icegatheringstatechange', check); clearTimeout(timeoutId); resolve(); };
      const check = () => { if (pc.iceGatheringState === 'complete') done(); };
      pc.addEventListener('icegatheringstatechange', check);
      timeoutId = setTimeout(done, timeoutMs);
      check();
    });
  };

  const endCall = () => {
    if (isRecording) {
      setIsRecording(false);
      setRecordingDuration(0);
      if (recordingIntervalRef.current) { clearInterval(recordingIntervalRef.current); recordingIntervalRef.current = null; }
    }
    if (localStreamRef.current) { localStreamRef.current.getTracks().forEach(track => track.stop()); localStreamRef.current = null; }
    if (peerConnectionRef.current) { peerConnectionRef.current.close(); peerConnectionRef.current = null; }
    disconnectLiveKitClient();
    if (audioElementRef.current) { audioElementRef.current.srcObject = null; }
    setIsCallActive(false); setCallStatus(''); setIsAudioPlaying(false);
  };

  const disconnectLiveKitClient = () => {
    const client = livekitClientRef.current;
    if (!client) return;
    livekitClientRef.current = null;
    client.disconnect().catch(() => {});
  };

  const disconnect = () => {
    try {
      if (isRecording) {
        setIsRecording(false); setRecordingDuration(0);
        const info = { id: `recording_${Date.now()}`, duration: recordingDuration * 1000, size: 0, startTime: Date.now() - (recordingDuration * 1000), endTime: Date.now(), format: 'audio/webm' };
        setRecordingInfo(info);
        if (recordingDuration > 0) { setRecordings(prev => [...prev, { ...info, timestamp: new Date().toISOString(), id: info.id }]); }
      }
      if (localStreamRef.current) { localStreamRef.current.getTracks().forEach(track => track.stop()); localStreamRef.current = null; }
      if (peerConnectionRef.current) { try { peerConnectionRef.current.getSenders().forEach(s => s.track && s.track.stop()); } catch (_) {} peerConnectionRef.current.close(); peerConnectionRef.current = null; }
      disconnectLiveKitClient();
      if (audioElementRef.current) { audioElementRef.current.srcObject = null; }
      setIsCallActive(false); setCallStatus(''); setIsAudioPlaying(false);
    } catch (error) {
      console.log('Error in disconnect:', error);
    }
  };

  const startCall = async () => {
    if (isCallActive || callStartingRef.current || isChatTestActive || submitLoading) return;
    callStartingRef.current = true;
    setIsCallStarting(true);
    setCallStatus('Connecting to voice agent…');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: { ideal: 1, max: 1 },
          sampleRate: { ideal: 48000 },
          sampleSize: { ideal: 16 },
          latency: { ideal: 0.01 }, // target ~10ms capture latency
          echoCancellation: { ideal: true },
          noiseSuppression: { ideal: true },
          autoGainControl: { ideal: true }
        }
      });
      localStreamRef.current = stream;
      // Apply constraints on the track as well (best-effort; browsers may ignore unsupported keys)
      const mic = stream.getAudioTracks()[0];
      if (mic) {
        try {
          await mic.applyConstraints({
            channelCount: 1,
            sampleRate: 48000,
            sampleSize: 16,
            latency: 0,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          });
        } catch (_) {}
        try { mic.contentHint = 'speech'; } catch (_) {}
      }

      // Start the session first: the response decides the transport (LiveKit or SmallWebRTC).
      const res = await server.browserTesting(client_id, selectedAgent.agent_id, email, 'voice-agents', undefined, selectedStatus);
      if (res.status !== 200) {
        try {
          const err = await res.json().catch(() => null);
          const message = err?.error || 'Failed to start browser call';
          showToast(message, 'error');
        } catch (_) {
          showToast('Failed to start browser call', 'error');
        }
        setIsCallActive(false);
        setCallStatus('Error in startCall');
        disconnect();
        return;
      }
      const resp = await res.json();
      const session = resp.data || resp;

      if (isLiveKitBrowserCall(session)) {
        // The Pipecat client captures the mic itself; the stream above only secured permission.
        stream.getTracks().forEach(track => track.stop());
        localStreamRef.current = null;
        livekitClientRef.current = await startLiveKitBrowserCall({
          session,
          audioElement: audioElementRef.current,
          onEnded: () => disconnect(),
        });
        try { await audioElementRef.current?.play(); } catch {}
        setIsRecording(true);
        setRecordingDuration(0);
        setIsCallActive(true);
        setCallStatus('Connected to voice agent');
        return;
      }

      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });
      peerConnectionRef.current = pc;

      // --- single audio transceiver + mic track ---
      const tx = pc.addTransceiver('audio', { direction: 'sendrecv' });
      // Prefer OPUS and de-prioritize/remove CN/DTMF for lower playout artifacts
      try {
        if (tx.setCodecPreferences && window.RTCRtpReceiver && RTCRtpReceiver.getCapabilities) {
          const caps = RTCRtpReceiver.getCapabilities('audio');
          if (caps && Array.isArray(caps.codecs)) {
            const filtered = caps.codecs.filter(c => {
              const mt = (c.mimeType || '').toLowerCase();
              return mt !== 'audio/telephone-event' && mt !== 'audio/cn';
            });
            const opusFirst = filtered.sort((a, b) => {
              const aOpus = (a.mimeType || '').toLowerCase() === 'audio/opus' ? 1 : 0;
              const bOpus = (b.mimeType || '').toLowerCase() === 'audio/opus' ? 1 : 0;
              return bOpus - aOpus;
            });
            tx.setCodecPreferences(opusFirst);
          }
        }
      } catch (e) {
        console.warn('setCodecPreferences failed (ignored):', e);
      }
      await tx.sender.replaceTrack(mic);

      // Disable DTX so silence still sends packets
      try {
        const p = tx.sender.getParameters();
        if (!p.encodings || p.encodings.length === 0) p.encodings = [{}];
        p.encodings[0].dtx = false;
        p.encodings[0].maxBitrate = 32000;
        await tx.sender.setParameters(p);
      } catch (e) {
        console.warn('setParameters failed (ok on some browsers):', e);
      }

      pc.ontrack = (e) => {
        const el = audioElementRef.current;
        if (!el) return;
        try { el.setAttribute('playsinline', 'true'); } catch (_) {}
        const [remote] = e.streams;
        if (!remote) return;
        if (el.srcObject !== remote) el.srcObject = remote;
        // Try to play immediately (gesture path)
        el.play?.().catch(() => {});
      };

      pc.onconnectionstatechange = () => {
        const s = pc.connectionState;
        if (s === 'disconnected' || s === 'failed' || s === 'closed') disconnect();
      };
      pc.oniceconnectionstatechange = () => {
        const s = pc.iceConnectionState;
        if (s === 'disconnected' || s === 'failed' || s === 'closed') disconnect();
      };

      await pc.setLocalDescription(await pc.createOffer());
      await waitForIceGatheringComplete(pc);

      const res2 = await fetch(session.connect_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sdp: pc.localDescription.sdp, type: pc.localDescription.type })
      });
      const answer = await res2.json();
      await pc.setRemoteDescription(answer);

      // extra nudge for autoplay
      try { await audioElementRef.current?.play(); } catch {}

      setIsRecording(true);
      setRecordingDuration(0);
      setIsCallActive(true);
      setCallStatus('Connected to voice agent');
    } catch (error) {
      console.error('Error in startCall:', error);
      setIsCallActive(false);
      setCallStatus('Error in startCall');
      disconnect();
    } finally {
      callStartingRef.current = false;
      setIsCallStarting(false);
    }
  };


  // Timers and cleanup
  useEffect(() => {
    if (isRecording) {
      recordingIntervalRef.current = setInterval(() => { setRecordingDuration(prev => prev + 1); }, 1000);
    } else {
      if (recordingIntervalRef.current) { clearInterval(recordingIntervalRef.current); recordingIntervalRef.current = null; }
    }
    return () => { if (recordingIntervalRef.current) { clearInterval(recordingIntervalRef.current); } };
  }, [isRecording]);

  useEffect(() => {
    return () => {
      if (localStreamRef.current) { localStreamRef.current.getTracks().forEach(track => track.stop()); }
      if (peerConnectionRef.current) { peerConnectionRef.current.close(); }
      if (livekitClientRef.current) { livekitClientRef.current.disconnect().catch(() => {}); livekitClientRef.current = null; }
      if (audioElementRef.current) { audioElementRef.current.srcObject = null; }
      if (recordingIntervalRef.current) { clearInterval(recordingIntervalRef.current); }
    };
  }, []);

  const handleSaveAsDraft = async () => {
    try {
      if(!changesOngoing){
        return;
      }
      setLoading(true);
      const agentToSave = {
        ...buildPersistableAgent(selectedAgent, inbuiltAnalyticsConfig, true, futureSchedulingConfig),
        agent_id: getAuthoritativeAgentId(),
      };
      if (!(await validateVoiceAgentConfiguration(agentToSave))) {
        return;
      }
      let obj = {
        content: agentToSave,
        agent_id: agentToSave?.agent_id || null,
        agent_name: agentToSave?.agent_name,
        phonenumber: agentToSave?.phonenumber,
        deploy_status: false,
        client_id: client_id,
        service_id: '',
        email: email,
        menu: 'voice-agents',
        submenu: 'agents',
      };
      const res = await server.save_voice_agent_saved_draft(obj);
      const resp = await res.json();
      if (res.status === 200) {
        setSelectedAgent(agentToSave);
        setDraftAgentData(deepClone(agentToSave));
        setBaselineAgent(deepClone(agentToSave));
        const savedAnalyticsConfig = analyticsConfigFromObject(agentToSave?.inbuilt_analytics, configData);
        const savedFutureSchedulingConfig = normalizeFutureSchedulingConfig(agentToSave?.future_scheduling);
        setInbuiltAnalyticsConfig(savedAnalyticsConfig);
        setBaselineInbuiltAnalyticsConfig(deepClone(savedAnalyticsConfig));
        setFutureSchedulingConfig(savedFutureSchedulingConfig);
        setBaselineFutureSchedulingConfig(deepClone(savedFutureSchedulingConfig));
        setSelectedStatus('Draft Version');
        setChangesOngoing(false);
        await getSavedDraft();
        showToast('Draft saved successfully!', 'success');
      } else {
        setConfigurationErrors(getConfigurationErrors(resp));
        const errorMessage = resp?.error?.message || (typeof resp?.error === 'string' ? resp.error : null) || Object.values(getConfigurationErrors(resp)).join(', ') || 'Error saving draft';
        showToast(errorMessage, 'error');
      }
    } catch (error) {
      console.log(error);
      showToast('Error saving draft', "error");
    } finally {
      setLoading(false);
      setDeployedStatus(false);
    }
  };
  const convert_object_to_array = (obj) => {
    if(!obj){
      return [];
    }
    return Object.entries(obj).map(([key, value]) => ({
      name: key,
      value: value
    }));
  };

  const buildAgentDiffConfig = (agent, analyticsConfig, useCurrentVariables = false, schedulingConfig = undefined) => (
    buildPersistableAgent(agent, analyticsConfig, useCurrentVariables, schedulingConfig) || {}
  );

  const getLoadedAgentConfigForDiff = () => (
    buildAgentDiffConfig(liveAgentData || initialAgentSetup || baselineAgent, analyticsConfigFromObject((liveAgentData || initialAgentSetup || baselineAgent)?.inbuilt_analytics, configData))
  );

  const getCurrentAgentConfigForDiff = () => {
    if (selectedStatus === 'Draft Version' && !changesOngoing && draftAgentData) {
      return buildAgentDiffConfig(draftAgentData, getDraftAnalyticsConfig());
    }
    return buildAgentDiffConfig(selectedAgent, inbuiltAnalyticsConfig, true, futureSchedulingConfig);
  };

  const getSavedDraft = async () => {
    try {
      let res = await server.get_voice_agent_saved_draft(client_id, '', email, 'voice-agents', 'agents', selectedAgent?.agent_id);
      if(res.status === 200){
        let resp = await res.json();
        setDraftMetadata(resp.metadata || null);
        if(resp.data){
          const draftData = resp.data;
          const draftNormalizedBase = { ...draftData, custom_tools: Array.isArray(draftData?.custom_tools) ? draftData.custom_tools : [] };
          const draftNormalized = {
            ...draftNormalizedBase,
            llm: normalizeSelectedLlm(draftNormalizedBase?.llm),
            knowledge_base: normalizeKnowledgeBase(draftNormalizedBase?.knowledge_base),
            vad_params: normalizeVadParams(draftNormalizedBase?.vad_params),
            transcription_controls: draftNormalizedBase?.transcription_controls
              ? normalizeTranscriptionControls(draftNormalizedBase.transcription_controls)
              : undefined,
            post_vad_wait_for_user: normalizePostVadWait(draftNormalizedBase?.post_vad_wait_for_user),
            conversation_controls: normalizeConversationControls(
              draftNormalizedBase?.conversation_controls,
            ),
            smart_turn_detection: normalizeSmartTurnDetection(draftNormalizedBase?.smart_turn_detection, draftNormalizedBase?.language),
            llm_turn_detection: normalizeLlmTurnDetection(draftNormalizedBase?.llm_turn_detection, configData?.llm_turn_detection),
            noise_cancellation: normalizeNoiseCancellation(draftNormalizedBase?.noise_cancellation, configData)
          };
          const normalizedDraft = normalizeAgentLanguageWiseMessages(draftNormalized);
          const { dynamicVariables: dynVars, variableConfigs: configs } = convertCustomVariablesToData(normalizedDraft?.custom_variables);
          setDynamicVariables(dynVars);
          setVariableConfigs(configs);
          setSelectedAgent(normalizedDraft);
          setDraftAgentData(deepClone(normalizedDraft));
          setBaselineAgent(deepClone(normalizedDraft));
          const draftAnalyticsConfig = analyticsConfigFromObject(normalizedDraft?.inbuilt_analytics, configData);
          setInbuiltAnalyticsConfig(draftAnalyticsConfig);
          setBaselineInbuiltAnalyticsConfig(deepClone(draftAnalyticsConfig));
          const draftFutureSchedulingConfig = normalizeFutureSchedulingConfig(normalizedDraft?.future_scheduling);
          setFutureSchedulingConfig(draftFutureSchedulingConfig);
          setBaselineFutureSchedulingConfig(deepClone(draftFutureSchedulingConfig));
        } else {
          // No saved draft, use initial agent setup or live data
          const fallbackData = initialAgentSetup || liveAgentData;
          if (fallbackData) {
            const normalized = { ...fallbackData, custom_tools: Array.isArray(fallbackData?.custom_tools) ? fallbackData.custom_tools : [] };
            normalized.llm = normalizeSelectedLlm(normalized?.llm);
            normalized.knowledge_base = normalizeKnowledgeBase(normalized?.knowledge_base);
            normalized.vad_params = normalizeVadParams(normalized?.vad_params);
            normalized.transcription_controls = normalized?.transcription_controls
              ? normalizeTranscriptionControls(normalized.transcription_controls)
              : undefined;
            normalized.post_vad_wait_for_user = normalizePostVadWait(normalized?.post_vad_wait_for_user);
            normalized.conversation_controls = normalizeConversationControls(
              normalized?.conversation_controls,
            );
            normalized.smart_turn_detection = normalizeSmartTurnDetection(normalized?.smart_turn_detection, normalized?.language);
            normalized.llm_turn_detection = normalizeLlmTurnDetection(normalized?.llm_turn_detection, configData?.llm_turn_detection);
            normalized.noise_cancellation = normalizeNoiseCancellation(normalized?.noise_cancellation, configData);
            const normalizedFallback = normalizeAgentLanguageWiseMessages(normalized);
            const { dynamicVariables: dynVars, variableConfigs: configs } = convertCustomVariablesToData(normalizedFallback?.custom_variables);
            setDynamicVariables(dynVars);
            setVariableConfigs(configs);
            setSelectedAgent(deepClone(normalizedFallback));
            setDraftAgentData(deepClone(normalizedFallback));
            setBaselineAgent(deepClone(normalizedFallback));
            const normalizedAnalyticsConfig = analyticsConfigFromObject(normalizedFallback?.inbuilt_analytics, configData);
            setInbuiltAnalyticsConfig(normalizedAnalyticsConfig);
            setBaselineInbuiltAnalyticsConfig(deepClone(normalizedAnalyticsConfig));
            const fallbackFutureSchedulingConfig = normalizeFutureSchedulingConfig(normalizedFallback?.future_scheduling);
            setFutureSchedulingConfig(fallbackFutureSchedulingConfig);
            setBaselineFutureSchedulingConfig(deepClone(fallbackFutureSchedulingConfig));
          }
          showToast('No saved draft found. Using default configuration.', "info");
        }
      }
    } catch (error) {
      console.log(error);
      showToast('Error loading draft', "error");
    }
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setSelectedStatus(newStatus);

    if(newStatus === 'Draft Version'){
      setDeployedStatus(false);
      getSavedDraft();
    } else if(newStatus === 'Live Version') {
      // Switch back to live data
      if (liveAgentData) {
        const { dynamicVariables: dynVars, variableConfigs: configs } = convertCustomVariablesToData(liveAgentData?.custom_variables);
        setDynamicVariables(dynVars);
        setVariableConfigs(configs);
        setSelectedAgent(deepClone(liveAgentData));
        setBaselineAgent(deepClone(liveAgentData));
        const liveAnalyticsConfig = analyticsConfigFromObject(liveAgentData?.inbuilt_analytics, configData);
        setInbuiltAnalyticsConfig(liveAnalyticsConfig);
        setBaselineInbuiltAnalyticsConfig(deepClone(liveAnalyticsConfig));
        const liveFutureSchedulingConfig = normalizeFutureSchedulingConfig(liveAgentData?.future_scheduling);
        setFutureSchedulingConfig(liveFutureSchedulingConfig);
        setBaselineFutureSchedulingConfig(deepClone(liveFutureSchedulingConfig));
        setDeployedStatus(liveAgentData?.is_active || false);
      } else {
        // If no live data, fetch it
        getAgentDetails(selectedAgent?.agent_id);
      }
    }
  };

  const smartTurnSupported = isSmartTurnSupportedForLanguage(selectedAgent?.language);
  const postVadConfig = normalizePostVadWait(selectedAgent?.post_vad_wait_for_user);
  const smartTurnConfig = normalizeSmartTurnDetection(selectedAgent?.smart_turn_detection, selectedAgent?.language);
  const llmTurnConfig = normalizeLlmTurnDetection(selectedAgent?.llm_turn_detection, configData?.llm_turn_detection);
  const isVoiceToVoice = selectedAgent?.pipeline_type === 'voice_to_voice';
  const transcriptionControlsSupported = supportsTranscriptionControls(selectedAgent);
  const transcriptionControlsConfig = normalizeTranscriptionControls(selectedAgent?.transcription_controls);
  const updateTranscriptionControlField = (field, value) => {
    updateSelectedAgent({
      ...selectedAgent,
      transcription_controls: {
        ...(selectedAgent?.transcription_controls || transcriptionControlsConfig),
        [field]: value
      }
    });
  };
  const renderTranscriptionControls = () => {
    if (!transcriptionControlsSupported) return null;
    return (
      <div className="pt-6">
        <h4 className="text-base font-medium text-gray-900 mb-2">Transcription Controls</h4>
        <p className="mb-4 text-sm text-gray-600">
          Controls how the selected realtime model detects the beginning and end of customer speech. Changes apply from the next call.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Pre-speech audio buffer</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={2000}
                step={50}
                value={selectedAgent?.transcription_controls?.pre_speech_audio_buffer_ms ?? transcriptionControlsConfig.pre_speech_audio_buffer_ms}
                onChange={(e) => updateTranscriptionControlField('pre_speech_audio_buffer_ms', e.target.value)}
                onBlur={() => updateTranscriptionControlField(
                  'pre_speech_audio_buffer_ms',
                  normalizeTranscriptionControls(selectedAgent?.transcription_controls).pre_speech_audio_buffer_ms
                )}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500"
              />
              <span className="text-sm text-gray-600">ms</span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Keeps audio from before speech detection. Increasing it can reduce clipped first syllables. Range: 0–2000 ms.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End-of-speech silence</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={100}
                max={3000}
                step={50}
                value={selectedAgent?.transcription_controls?.end_of_speech_silence_ms ?? transcriptionControlsConfig.end_of_speech_silence_ms}
                onChange={(e) => updateTranscriptionControlField('end_of_speech_silence_ms', e.target.value)}
                onBlur={() => updateTranscriptionControlField(
                  'end_of_speech_silence_ms',
                  normalizeTranscriptionControls(selectedAgent?.transcription_controls).end_of_speech_silence_ms
                )}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500"
              />
              <span className="text-sm text-gray-600">ms</span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Silence required before the provider considers the customer finished. Range: 100–3000 ms.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Speech-start sensitivity</label>
            <ModernTimeDropdown
              value={transcriptionControlsConfig.speech_start_sensitivity === 'high' ? 'High' : 'Low'}
              onChange={(e) => updateTranscriptionControlField('speech_start_sensitivity', e.target.value.toLowerCase())}
              options={['High', 'Low']}
              width="w-full"
            />
            <p className="mt-1 text-xs text-gray-500">
              High detects soft or short speech more easily. Low filters more noise.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Speech-end sensitivity</label>
            <ModernTimeDropdown
              value={transcriptionControlsConfig.speech_end_sensitivity === 'high' ? 'High' : 'Low'}
              onChange={(e) => updateTranscriptionControlField('speech_end_sensitivity', e.target.value.toLowerCase())}
              options={['High', 'Low']}
              width="w-full"
            />
            <p className="mt-1 text-xs text-gray-500">
              High ends speech more readily. Low is more patient with quiet or slow speakers.
            </p>
          </div>
        </div>
      </div>
    );
  };
  const llmModelConfig = configData?.llm?.[selectedAgent?.llm?.provider]?.[selectedAgent?.llm?.model];
  const llmReasoningConfig = llmModelConfig?.reasoning_effort?.available === 1
    ? llmModelConfig.reasoning_effort
    : null;
  const llmReasoningOptions = Array.isArray(llmReasoningConfig?.options)
    ? llmReasoningConfig.options
    : [];
  const selectedReasoningEffort = normalizeSelectedLlm(selectedAgent?.llm).reasoning_effort || 'none';
  const noiseCancellationAvailable = Boolean(
    configData?.noise_cancellation?.default
    && Array.isArray(configData?.noise_cancellation?.processors),
  );
  const noiseCancellationCapability = getNoiseCancellationCapability(configData);
  const noiseCancellation = normalizeNoiseCancellation(selectedAgent?.noise_cancellation, configData);
  const processorOptions = getNoiseCancellationProcessorOptions(configData);
  const processorLabels = processorOptions.map(option => option.label);
  const selectedProcessorLabel = processorOptions.find(
    option => option.value === noiseCancellation.processor?.engine,
  )?.label || 'None';
  const aiCousticsCapability = getAiCousticsCapability(configData);
  const aiCousticsModels = aiCousticsCapability?.models || [];
  const aiCousticsModelLabels = aiCousticsModels.map(model => model.label);
  const showAiCousticsSettings = noiseCancellation.processor?.engine === 'ai_acoustics';
  const showNoiseCancellationSensitivity = showAiCousticsSettings
    && modelSupportsSensitivity(noiseCancellation.processor.model, configData);

  // UI
  return (
    <div className="py-3 px-5 font-sans bg-gray-50">
      <div className="flex items-start gap-4 mb-8">
        <button onClick={() => navigate(-1)} className="p-2 mt-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold text-gray-900">Configure Agent</h1>
          <p className="text-gray-600 mt-1 text-sm">{isCreating ? 'Set up a new voice agent' : `Editing ${selectedAgent?.agent_name || ''}`}</p>
          {!isCreating && (
            <ConfigurationModifiedTime
              metadata={selectedStatus === 'Draft Version' ? draftMetadata : liveMetadata}
              label={selectedStatus === 'Draft Version' ? 'Draft last modified' : 'Live last modified'}
            />
          )}
          <div className="flex flex-wrap items-start gap-2 mt-3">
            {role === 'superadmin' && (
              <div className="flex items-center gap-2 mr-auto shrink-0">
                <label className="cursor-pointer shrink-0">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleUploadConfig}
                    className="hidden"
                    id="voice-agent-config-upload"
                  />
                  <button
                    type="button"
                    className="h-9 px-3 bg-blue-100 text-blue-700 rounded-lg border border-blue-300 hover:bg-blue-200 flex items-center whitespace-nowrap text-sm"
                    onClick={() => document.getElementById('voice-agent-config-upload').click()}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Upload Config
                  </button>
                </label>

                <button
                  type="button"
                  className="h-9 px-3 bg-green-100 text-green-700 rounded-lg border border-green-300 hover:bg-green-200 flex items-center whitespace-nowrap text-sm shrink-0"
                  onClick={handleDownloadConfig}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download Config
                </button>
              </div>
            )}
            <div className="flex flex-wrap items-start justify-end gap-2 ml-auto">
            <div>
              <ModernTimeDropdown value={selectedStatus} onChange={handleStatusChange} options={['Live Version', 'Draft Version']} width={'w-48'} height={'h-9'} />
            </div>
            <div>
              <button 
                onClick={handleSaveAsDraft} 
                disabled={!changesOngoing} 
                className={`${!changesOngoing ? 'bg-gray-500 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} h-9 text-white px-4 rounded-md text-sm whitespace-nowrap transition-colors`}
              >
                Save as Draft
              </button>
            </div>
            <ConfigurationDiffButton
              title="Agent Changes"
              originalConfig={getLoadedAgentConfigForDiff}
              modifiedConfig={getCurrentAgentConfigForDiff}
              buttonClassName="text-sm h-9"
              disabled={!selectedAgent}
            />
            <div className="relative flex items-center">
              {!isCallActive && !isCallStarting && !isChatTestActive && !showOutboundModal && !submitLoading && (
                <div>
                  <ModernTimeDropdown placeholder={'Test Workflow'} value={testWorkflowOption} onChange={handleTestWorkflowChange} options={['Test in Browser', 'Test Outbound', ...(canTestChat(selectedAgent, configData) && ['superadmin', 'admin', 'manager'].includes(String(role).toLowerCase()) ? ['Test Chat'] : [])]} width={'w-48'} height={'h-9'} backgroundColor={'bg-indigo-600'} />
                </div>
              )}
              {isCallActive && (
                <button onClick={disconnect} className="h-9 w-48 bg-red-600 hover:bg-red-700 text-white px-4 rounded-lg text-sm transition-colors">End Call</button>
              )}
              {isCallStarting && <button type="button" disabled className="h-9 w-48 bg-indigo-600 text-white px-4 rounded-lg text-sm opacity-70">Connecting…</button>}
              {isChatTestActive && <button type="button" onClick={() => setTestChatOpen(true)} className="h-9 w-48 bg-indigo-600 hover:bg-indigo-700 text-white px-4 rounded-lg text-sm">Resume Test Chat</button>}
              {(showOutboundModal || submitLoading) && <button type="button" disabled className="h-9 w-48 bg-indigo-600 text-white px-4 rounded-lg text-sm opacity-70">{submitLoading ? 'Starting outbound…' : 'Test Outbound'}</button>}
              <audio ref={audioElementRef} id="remoteAudio" autoPlay playsInline controls preload="auto" style={{ display: 'none' }} onPlay={() => setIsAudioPlaying(true)} onPause={() => setIsAudioPlaying(false)} onError={() => setIsAudioPlaying(false)} />
              {testChatTarget && (
                <TestChatModal
                  key={`${client_id}:${testChatTarget.agent_id}:${testChatRun}`}
                  context={{ client_id, email, menu: 'voice-agents' }}
                  target={testChatTarget}
                  isNewAgent={isCreating || !liveAgentData}
                  isOpen={testChatOpen}
                  onClose={() => setTestChatOpen(false)}
                  onOpen={() => setTestChatOpen(true)}
                  onActivityChange={setIsChatTestActive}
                  onNewTest={() => { setTestChatTarget(getCurrentChatTestTarget()); setTestChatRun(value => value + 1); }}
                />
              )}
              {showOutboundModal && (
                <div style={{zIndex: '9999999999', position: 'fixed', top: '0', left: '0', height: '100%', width: '100%', background: 'rgba(0,0,0, 0.5)'}}>
                  <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-lg relative">
                      <div className="flex justify-between items-center p-4 pb-2">
                        <h2 className="text-lg text-gray-800" style={{fontWeight: '550'}}>Test Outbound</h2>
                        <button onClick={() => setShowOutboundModal(false)} className="text-gray-500 hover:text-gray-700"><FaTimes style={{color: 'slategray'}}/></button>
                      </div>
                      <div className="p-6 pt-2 text-sm text-gray-600 h-[100px] flex gap-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Country Code</label>
                      <ModernTimeDropdown value={countryCode} onChange={(e) => setCountryCode(e.target.value)} options={['+91 (INDIA)']} width='w-full'/>
                        </div>
                      <InputWithCharacterCount
                          label="Phone Number"
                          value={outboundPhoneNumber}
                          onChange={(e) => setOutboundPhoneNumber(e.target.value)}
                          placeholder="Enter phone number"
                          maxLength={10}
                        />
                      </div>
                      <div className="flex justify-end gap-3 p-6">
                        <button onClick={() => handleSubmitOutbound()} disabled={submitLoading} className={`${submitLoading ? 'bg-gray-500 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} px-6 py-1 rounded-lg text-white`} style={{height: '40px', borderRadius: '4px'}}>Submit</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div>
                {deployedStatus ? (
                  <button
                    disabled={changesOngoing}
                    onClick={() => handleSaveAgent()}
                    className={`h-9 px-4 text-sm text-white rounded-md ${changesOngoing ? 'bg-gray-500 hover:bg-gray-600' : 'bg-red-600 hover:bg-red-700'}`}
                  >
                    Undeploy
                  </button>
                ) : (
                  <ConfigurationDiffButton
                    title="Agent Changes"
                    originalConfig={getLoadedAgentConfigForDiff}
                    modifiedConfig={getCurrentAgentConfigForDiff}
                    triggerLabel="Deploy"
                    showTriggerIcon={false}
                    disabled={changesOngoing || !selectedAgent}
                    triggerClassName={`px-4 h-9 text-sm ${changesOngoing ? 'bg-gray-500 hover:bg-gray-600' : 'bg-green-600 hover:bg-green-700'} text-white rounded-md focus:outline-none`}
                    onNext={() => setShowDeployConfirmation(true)}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-end items-center gap-2 mt-2">
              {callStatus && (
                <div className={`text-xs px-2 py-1 rounded ${callStatus.includes('Error') ? 'bg-red-100 text-red-800' : callStatus.includes('Connected') || callStatus.includes('established') ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>{callStatus}</div>
              )}
              {isCallActive && isRecording && (
                <div className="text-xs px-2 py-1 rounded bg-red-100 text-red-800">Recording: {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}</div>
              )}
          </div>
        </div>
      </div>
      <ErrorSummary errors={configurationErrors} />
      {uploadConfirmation && (
        <div
          style={{ zIndex: '9999999999', position: 'fixed', top: 0, left: 0, height: '100%', width: '100%', background: 'rgba(0,0,0, 0.5)' }}
          onKeyDown={(event) => {
            if (event.key === 'Tab') {
              const buttons = event.currentTarget.querySelectorAll('button');
              const first = buttons[0];
              const last = buttons[buttons.length - 1];
              if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
              } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
              }
            }
            if (event.key === 'Escape') {
              uploadConfirmation.resolve(false);
              setUploadConfirmation(null);
            }
          }}
        >
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg relative" role="dialog" aria-modal="true" aria-labelledby="upload-confirmation-title" aria-describedby="upload-confirmation-message">
            <div className="flex justify-between items-center p-4 pb-2">
              <h2 id="upload-confirmation-title" className="text-lg text-gray-800" style={{ fontWeight: '550' }}>Upload configuration?</h2>
            </div>
            <div id="upload-confirmation-message" className="p-6 pt-2 text-sm text-gray-700">
              <p>{uploadConfirmation.message}</p>
              <p className="mt-3">Cancel keeps your current edits. Continuing loads the file into the editor; it does not deploy it.</p>
            </div>
            <div className="flex justify-end gap-3 p-6 pt-0">
              <button
                autoFocus
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none"
                style={{ height: '40px', borderRadius: '4px' }}
                onClick={() => { uploadConfirmation.resolve(false); setUploadConfirmation(null); }}
              >Cancel</button>
              <button
                className="px-4 py-2 text-white rounded focus:outline-none bg-indigo-600 hover:bg-indigo-700"
                style={{ height: '40px', borderRadius: '4px' }}
                onClick={() => { uploadConfirmation.resolve(true); setUploadConfirmation(null); }}
              >Continue upload</button>
            </div>
          </div>
          </div>
        </div>
      )}
      <DeployConfirmationModal
        isOpen={showDeployConfirmation}
        confirmationText={`Changes will be deployed in ${client_id || 'N/A'} and ${selectedAgent?.agent_name || 'N/A'}`}
        onClose={() => setShowDeployConfirmation(false)}
        onConfirm={(versionDescription) => {
          setShowDeployConfirmation(false);
          handleSaveAgent(versionDescription);
        }}
      />

      {!loading && selectedAgent && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex flex-wrap" role="tablist" aria-label="Agent details tabs">
              {AGENT_DETAILS_TABS.map(({ id, label }) => (
                <button
                  key={id}
                  id={`agent-tab-${id}`}
                  type="button"
                  role="tab"
                  aria-selected={agentDetailsTab === id}
                  aria-controls={`agent-panel-${id}`}
                  onClick={() => setAgentDetailsTab(id)}
                  className={`py-4 px-4 text-center border-b-2 font-medium text-sm ${
                    agentDetailsTab === id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>
          </div>

                    <div id="agent-panel-overview" role="tabpanel" aria-labelledby="agent-tab-overview" hidden={agentDetailsTab !== 'overview'} className="p-6 space-y-6 [&>:first-child]:border-t-0 [&>:first-child]:pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Agent ID</label>
                <input type="text" value={selectedAgent?.agent_id || 'Auto-generated'} disabled className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600" />
              </div>
              <div>
              <InputWithCharacterCount
                  label="Agent Name"
                  value={selectedAgent?.agent_name}
                  onChange={(e) => updateSelectedAgent({ ...selectedAgent, agent_name: e.target.value })}
                  placeholder="Enter agent name"
                  maxLength={CHARACTER_LIMITS.AGENT_NAME}
                  required
                />
              </div>
            </div>

            <div>
            <TextareaWithCharacterCount
                label="Description"
                value={selectedAgent?.description}
                onChange={(e) => updateSelectedAgent({ ...selectedAgent, description: e.target.value })}
                placeholder="Describe what this agent does"
                rows={3}
                maxLength={CHARACTER_LIMITS.DESCRIPTION}
              />
            </div>



            <div className="grid grid-cols-1 md:grid-cols-2 gap-6"><div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
              <ModernTimeDropdown value={selectedAgent?.language || ''} onChange={(e) => updateSelectedAgent({ ...selectedAgent, language: e.target.value })} options={languageOptions} width="w-full" />
            </div>
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Languages (optional)</label>
              <ModernMultiSelect
                value={(selectedAgent?.secondary_languages || []).filter(l => l && l !== selectedAgent?.language)}
                onChange={(next) => {
                  const primary = selectedAgent?.language;
                  const deduped = Array.from(new Set((next || []).filter(l => l && l !== primary)));
                  updateSelectedAgent({ ...selectedAgent, secondary_languages: deduped });
                }}
                options={secondaryLanguageOptions.filter(opt => opt !== selectedAgent?.language)}
                width="w-full"
              />
            </div></div>

            <div className="border-t pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number (Outbound)</label>
              <ModernTimeDropdown options={phoneNumbers} value={selectedAgent?.phonenumber || ''} onChange={(e) => updateSelectedAgent({ ...selectedAgent, phonenumber: e.target.value })} width="w-full" />
            </div>

            <MessageWithOverrides
              label="First Message"
              value={selectedAgent?.first_message}
              onChange={(e) => updateSelectedAgent({ ...selectedAgent, first_message: e.target.value })}
              placeholder="The first message the agent will say. If empty, the agent will wait for the user to start."
              rows={2}
              maxLength={CHARACTER_LIMITS.FIRST_MESSAGE}
              systemVariables={systemVariables}
              dynamicVariables={dynamicVariables}
              onAddDynamicVariable={handleAddDynamicVariable}
              overrideMaxLength={CHARACTER_LIMITS.FIRST_MESSAGE}
              rules={getAgentMessageRules(selectedAgent, null, 'first_message')}
              modes={['voice', 'chat']}
              availableLanguages={getAvailableLanguageKeys(selectedAgent)}
              onRulesChange={rules => updateSelectedAgent(setAgentMessageRules(selectedAgent, null, 'first_message', rules))}
            />





            <VariableConfigurationTable
            systemVariables={systemVariables}
              dynamicVariables={dynamicVariables}
              onUpdateVariableConfig={handleVariableConfigUpdate}
              variableConfigs={variableConfigs}
              variableConfiguration={selectedAgent?.variable_configuration || { url: '', header: '', method: 'GET' }}
              onBaseConfigChange={(key, value) => {
                const vc = { ...(selectedAgent?.variable_configuration || {}) };
                vc[key] = value;
                updateSelectedAgent({ ...selectedAgent, variable_configuration: vc });
              }}
            />

            <MessageRulesEditor label="System Prompt suffixes" maxLength={30000} availableLanguages={getAvailableLanguageKeys(selectedAgent)}
              value={selectedAgent?.system_prompt_suffix_rules || []}
              onChange={rules => updateSelectedAgent({ ...selectedAgent, system_prompt_suffix_rules: rules })}
              systemVariables={systemVariables} dynamicVariables={dynamicVariables} onAddDynamicVariable={handleAddDynamicVariable}
              renderMessage={action => <TextareaWithVariables
              headerActions={action}
              label="System Prompt"
              value={selectedAgent?.system_prompt}
              onChange={(e) => updateSelectedAgent({ ...selectedAgent, system_prompt: e.target.value })}
              placeholder="Enter the system prompt that defines the agent's behavior."
              rows={24}
              maxLength={CHARACTER_LIMITS.SYSTEM_PROMPT}
              systemVariables={systemVariables}
              dynamicVariables={dynamicVariables}
              onAddDynamicVariable={handleAddDynamicVariable}
            />} />
          </div>
          <div id="agent-panel-pipeline" role="tabpanel" aria-labelledby="agent-tab-pipeline" hidden={agentDetailsTab !== 'pipeline'} className="p-6 space-y-6 [&>:first-child]:border-t-0 [&>:first-child]:pt-0">
            <div>
              <h3 className="mb-4 text-lg font-medium text-gray-900">Pipeline Type</h3>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => updateSelectedAgent(changeAgentPipeline(selectedAgent, 'stt_llm_tts'))}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${!isVoiceToVoice ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  STT → LLM → TTS
                </button>
                <button
                  type="button"
                  onClick={() => updateSelectedAgent(changeAgentPipeline(selectedAgent, 'voice_to_voice'))}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${isVoiceToVoice ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  Voice to Voice
                </button>
              </div>
            </div>

            <ConfigurationSection title="Turn Detection" hasErrors={hasConfigurationError("vad_params", "post_vad_wait_for_user", "smart_turn_detection", "llm_turn_detection")}>
              <div className="space-y-6">
                <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Local VAD Params</h3>
              <p className="mb-4 text-sm text-gray-600">
                Controls local pipeline voice detection. These values do not configure provider-managed speech boundaries.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {VAD_FIELD_CONFIG.map((field) => {
                  const currentValue = normalizeVadValue(selectedAgent?.vad_params?.[field.key], VAD_DEFAULTS[field.key]);
                  return (
                    <div key={field.key}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min={0}
                          max={1}
                          step={0.05}
                          value={currentValue}
                          onChange={(e) => {
                            const val = normalizeVadValue(e.target.value, VAD_DEFAULTS[field.key]);
                            updateSelectedAgent({
                              ...selectedAgent,
                              vad_params: {
                                ...normalizeVadParams(selectedAgent?.vad_params),
                                [field.key]: val
                              }
                            });
                          }}
                          className="w-full"
                        />
                        <div className="text-sm text-gray-600" style={{ width: '56px', textAlign: 'right' }}>
                          {currentValue.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Post VAD Wait for User</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Enabled</label>
                  <ModernTimeDropdown
                    value={postVadConfig.enabled}
                    onChange={(e) => {
                      updateSelectedAgent({
                        ...selectedAgent,
                        post_vad_wait_for_user: {
                          ...postVadConfig,
                          enabled: e.target.value
                        }
                      });
                    }}
                    options={['No', 'Yes']}
                    width="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Wait Duration (seconds)</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={postVadConfig.secs}
                      onChange={(e) => {
                        const parsed = Number(e.target.value);
                        const val = Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : POST_VAD_WAIT_DEFAULTS.secs;
                        updateSelectedAgent({
                          ...selectedAgent,
                          post_vad_wait_for_user: {
                            ...postVadConfig,
                            secs: val
                          }
                        });
                      }}
                      className="w-full"
                      disabled={postVadConfig.enabled !== 'Yes'}
                    />
                    <div className="text-sm text-gray-600" style={{ width: '56px', textAlign: 'right' }}>
                      {postVadConfig.secs.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {!isVoiceToVoice && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Smart Turn Detection</h3>
              <div className="mb-3 text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
                Smart turn detection is recommended to use only when selected language is English & Hindi.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Enabled</label>
                  <ModernTimeDropdown
                    value={smartTurnConfig.enabled}
                    onChange={(e) => {
                      const nextEnabled = e.target.value;
                      updateSelectedAgent({
                        ...selectedAgent,
                        smart_turn_detection: {
                          ...smartTurnConfig,
                          enabled: nextEnabled
                        }
                      });
                    }}
                    options={['No', 'Yes']}
                    width="w-full"
                    isDisabled={!smartTurnSupported}
                  />
                  {!smartTurnSupported && (
                    <div className="mt-1 text-xs text-gray-500">Not available for the selected language.</div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Smart Turn Timeout (seconds)</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min={0}
                      max={3}
                      step={0.05}
                      value={smartTurnConfig.smart_turn_timeout}
                      onChange={(e) => {
                        const val = normalizeSmartTurnTimeout(e.target.value, SMART_TURN_DEFAULTS.smart_turn_timeout);
                        updateSelectedAgent({
                          ...selectedAgent,
                          smart_turn_detection: {
                            ...smartTurnConfig,
                            smart_turn_timeout: val
                          }
                        });
                      }}
                      className="w-full"
                      disabled={!smartTurnSupported || smartTurnConfig.enabled !== 'Yes'}
                    />
                    <div className="text-sm text-gray-600" style={{ width: '56px', textAlign: 'right' }}>
                      {smartTurnConfig.smart_turn_timeout.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">LLM Turn Detection</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Enabled</label>
                  <ModernTimeDropdown
                    value={llmTurnConfig.enabled}
                    onChange={(e) => updateSelectedAgent({ ...selectedAgent, llm_turn_detection: { ...llmTurnConfig, enabled: e.target.value } })}
                    options={['No', 'Yes']}
                    width="w-full"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <TextareaWithCharacterCount
                  label="Instruction"
                  value={llmTurnConfig.instruction}
                  onChange={(e) => updateSelectedAgent({ ...selectedAgent, llm_turn_detection: { ...llmTurnConfig, instruction: e.target.value } })}
                  maxLength={CHARACTER_LIMITS.LLM_TURN_DETECTION_INSTRUCTION}
                  placeholder="Custom main turn-detection instruction..."
                  disabled={llmTurnConfig.enabled !== 'Yes'}
                  rows={3}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Incomplete Short Timeout (seconds)</label>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={llmTurnConfig.incomplete_short_timeout ?? ''}
                      onChange={(e) => {
                        const val = e.target.value === '' ? null : Number(e.target.value);
                        updateSelectedAgent({ ...selectedAgent, llm_turn_detection: { ...llmTurnConfig, incomplete_short_timeout: val } });
                      }}
                      disabled={llmTurnConfig.enabled !== 'Yes'}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500 ${llmTurnConfig.enabled !== 'Yes' ? 'bg-gray-50 text-gray-600' : ''}`}
                      placeholder="e.g. 3.5"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Incomplete Long Timeout (seconds)</label>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={llmTurnConfig.incomplete_long_timeout ?? ''}
                      onChange={(e) => {
                        const val = e.target.value === '' ? null : Number(e.target.value);
                        updateSelectedAgent({ ...selectedAgent, llm_turn_detection: { ...llmTurnConfig, incomplete_long_timeout: val } });
                      }}
                      disabled={llmTurnConfig.enabled !== 'Yes'}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500 ${llmTurnConfig.enabled !== 'Yes' ? 'bg-gray-50 text-gray-600' : ''}`}
                      placeholder="e.g. 8.0"
                    />
                  </div>
                </div>
                <TextareaWithCharacterCount
                  label="Short Timeout Restart Instruction"
                  value={llmTurnConfig.short_timeout_restart_instruction}
                  onChange={(e) => updateSelectedAgent({ ...selectedAgent, llm_turn_detection: { ...llmTurnConfig, short_timeout_restart_instruction: e.target.value } })}
                  maxLength={CHARACTER_LIMITS.LLM_TURN_DETECTION_INSTRUCTION}
                  placeholder="Custom prompt used after short incomplete timeout expires..."
                  disabled={llmTurnConfig.enabled !== 'Yes'}
                  rows={3}
                />
                <TextareaWithCharacterCount
                  label="Long Timeout Restart Instruction"
                  value={llmTurnConfig.long_timeout_restart_instruction}
                  onChange={(e) => updateSelectedAgent({ ...selectedAgent, llm_turn_detection: { ...llmTurnConfig, long_timeout_restart_instruction: e.target.value } })}
                  maxLength={CHARACTER_LIMITS.LLM_TURN_DETECTION_INSTRUCTION}
                  placeholder="Custom prompt used after long incomplete timeout expires..."
                  disabled={llmTurnConfig.enabled !== 'Yes'}
                  rows={3}
                />
              </div>
            </div></div>
            </ConfigurationSection>

            {noiseCancellationAvailable && (
              <ConfigurationSection title="Noise Cancellation" hasErrors={hasConfigurationError("noise_cancellation")}>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telephony noise cancellation</label>
                    <ModernTimeDropdown
                      value={noiseCancellation.telephony_enabled ? 'Yes' : 'No'}
                      onChange={(e) => updateSelectedAgent({
                        ...selectedAgent,
                        noise_cancellation: setTelephonyNoiseCancellation(
                          noiseCancellation,
                          e.target.value === 'Yes',
                          configData,
                        ),
                      })}
                      options={['Yes', 'No']}
                      width="w-full"
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      {noiseCancellationCapability.telephony.informational_text}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Audio processor</label>
                    <ModernTimeDropdown
                      value={selectedProcessorLabel}
                      onChange={(e) => {
                        const engine = processorOptions.find(
                          option => option.label === e.target.value,
                        )?.value;
                        updateSelectedAgent({
                          ...selectedAgent,
                          noise_cancellation: setNoiseCancellationProcessor(
                            noiseCancellation,
                            engine,
                            configData,
                          ),
                        });
                      }}
                      options={processorLabels}
                      width="w-full"
                    />
                  </div>

                  {showAiCousticsSettings && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                      <ModernTimeDropdown
                        value={getAiCousticsModelLabel(noiseCancellation.processor.model, configData)}
                        onChange={(e) => updateSelectedAgent({
                          ...selectedAgent,
                          noise_cancellation: normalizeNoiseCancellation({
                            telephony_enabled: noiseCancellation.telephony_enabled,
                            processor: {
                              ...noiseCancellation.processor,
                              model: getAiCousticsModelFromLabel(e.target.value, configData),
                            },
                          }, configData),
                        })}
                        options={aiCousticsModelLabels}
                        width="w-full"
                      />
                    </div>
                  )}
                {showNoiseCancellationSensitivity && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sensitivity</label>
                    <input
                      type="number"
                      min={aiCousticsCapability?.sensitivity_min ?? 0}
                      max={aiCousticsCapability?.sensitivity_max ?? 1}
                      step={aiCousticsCapability?.sensitivity_step ?? 0.01}
                      value={selectedAgent?.noise_cancellation?.processor?.sensitivity ?? noiseCancellation.processor.sensitivity}
                      onChange={(e) => updateSelectedAgent({
                        ...selectedAgent,
                        noise_cancellation: {
                          telephony_enabled: noiseCancellation.telephony_enabled,
                          processor: {
                            ...noiseCancellation.processor,
                            sensitivity: e.target.value,
                          },
                        },
                      })}
                      onBlur={(e) => updateSelectedAgent({
                        ...selectedAgent,
                        noise_cancellation: normalizeNoiseCancellation({
                          telephony_enabled: noiseCancellation.telephony_enabled,
                          processor: {
                            ...noiseCancellation.processor,
                            sensitivity: e.target.value,
                          },
                        }, configData),
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Enter a value from 0 to 1. Default: {aiCousticsCapability?.default_sensitivity ?? 0.25}.
                    </p>
                  </div>
                )}
                </div>
              </ConfigurationSection>
            )}

            {!isVoiceToVoice && (
            <ConfigurationSection title="Base Model" hasErrors={hasConfigurationError("llm")}>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                  <ModernTimeDropdown value={selectedAgent?.llm?.provider || ''} onChange={(e) => {
                    const newProvider = e.target.value; updateModelOptions(newProvider, 'llm');
                    let firstModel = ''; if (configData.llm && configData.llm[newProvider]) { const models = Object.keys(configData.llm[newProvider]); if (models.length > 0) { firstModel = models[0]; } }
                    updateSelectedAgent({ ...selectedAgent, llm: normalizeSelectedLlm({ provider: newProvider, model: firstModel }) });
                  }} options={llmProviderOptions} width="w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                  <ModernTimeDropdown value={selectedAgent?.llm?.model || ''} onChange={(e) => {
                    const newModel = e.target.value;
                    updateSelectedAgent({
                      ...selectedAgent,
                      llm: normalizeSelectedLlm({
                        ...selectedAgent.llm,
                        model: newModel,
                        reasoning_effort: undefined
                      })
                    });
                  }} options={llmModelOptions} width="w-full" />
                </div>
                {llmReasoningConfig && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reasoning Effort</label>
                    <ModernTimeDropdown
                      value={selectedReasoningEffort}
                      onChange={(e) => updateSelectedAgent({
                        ...selectedAgent,
                        llm: normalizeSelectedLlm({
                          ...selectedAgent.llm,
                          reasoning_effort: e.target.value
                        })
                      })}
                      options={llmReasoningOptions}
                      width="w-full"
                    />
                  </div>
                )}
              </div>
            </ConfigurationSection>
            )}

            {isVoiceToVoice && (
              <ConfigurationSection title="Voice to Voice" hasErrors={hasConfigurationError("voice_to_voice")}>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                    <ModernTimeDropdown
                      value={selectedAgent?.voice_to_voice?.provider || ''}
                      onChange={(e) => {
                        const newProvider = e.target.value;
                        updateV2vModelOptions(newProvider, selectedV2vGender);
                        const models = Object.keys(configData?.voice_to_voice?.[newProvider] || {});
                        const firstModel = models[0] || '';
                        const voiceList = configData?.voice_to_voice?.[newProvider]?.[firstModel]?.voices?.[selectedV2vGender] || [];
                        const firstVoice = voiceList[0]?.id || '';
                        const speedCfg = configData?.voice_to_voice?.[newProvider]?.[firstModel]?.speed;
                        const resolvedSpeed = speedCfg?.available === 1 ? (speedCfg.default_value ?? 1.0) : 1.0;
                        updateSelectedAgent({ ...selectedAgent, voice_to_voice: { provider: newProvider, model: firstModel, voice: firstVoice, gender: selectedV2vGender, speed: resolvedSpeed } });
                      }}
                      options={v2vProviderOptions}
                      width="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                    <ModernTimeDropdown
                      value={selectedAgent?.voice_to_voice?.model || ''}
                      onChange={(e) => {
                        const newModel = e.target.value;
                        const provider = selectedAgent?.voice_to_voice?.provider;
                        updateV2vVoiceOptions(provider, newModel, selectedV2vGender);
                        const voiceList = configData?.voice_to_voice?.[provider]?.[newModel]?.voices?.[selectedV2vGender] || [];
                        const firstVoice = voiceList[0]?.id || '';
                        const speedCfg = configData?.voice_to_voice?.[provider]?.[newModel]?.speed;
                        const resolvedSpeed = speedCfg?.available === 1 ? (speedCfg.default_value ?? 1.0) : 1.0;
                        updateSelectedAgent({ ...selectedAgent, voice_to_voice: { ...selectedAgent.voice_to_voice, model: newModel, voice: firstVoice, speed: resolvedSpeed } });
                      }}
                      options={v2vModelOptions}
                      width="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                    <ModernTimeDropdown
                      value={selectedV2vGender}
                      onChange={(e) => {
                        const newGender = e.target.value;
                        setSelectedV2vGender(newGender);
                        const provider = selectedAgent?.voice_to_voice?.provider;
                        const model = selectedAgent?.voice_to_voice?.model;
                        updateV2vVoiceOptions(provider, model, newGender);
                        const voiceList = configData?.voice_to_voice?.[provider]?.[model]?.voices?.[newGender] || [];
                        const firstVoice = voiceList[0]?.id || '';
                        updateSelectedAgent({ ...selectedAgent, voice_to_voice: { ...selectedAgent.voice_to_voice, gender: newGender, voice: firstVoice } });
                      }}
                      options={['female', 'male']}
                      width="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Voice</label>
                    <ModernTimeDropdown
                      value={(() => {
                        const { provider, model, gender, voice } = selectedAgent?.voice_to_voice || {};
                        const voiceList = configData?.voice_to_voice?.[provider]?.[model]?.voices?.[gender || selectedV2vGender] || [];
                        const voiceObj = voiceList.find(v => typeof v === 'object' && v.id === voice);
                        return voiceObj ? voiceObj.name : (voice || '');
                      })()}
                      onChange={(e) => {
                        const voiceName = e.target.value;
                        const { provider, model, gender } = selectedAgent?.voice_to_voice || {};
                        const voiceList = configData?.voice_to_voice?.[provider]?.[model]?.voices?.[gender || selectedV2vGender] || [];
                        const voiceObj = voiceList.find(v => typeof v === 'object' && v.name === voiceName);
                        const voiceId = voiceObj ? voiceObj.id : voiceName;
                        updateSelectedAgent({ ...selectedAgent, voice_to_voice: { ...selectedAgent.voice_to_voice, voice: voiceId } });
                      }}
                      options={v2vVoiceOptions}
                      width="w-full"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Speed</label>
                  {(() => {
                    const provider = selectedAgent?.voice_to_voice?.provider;
                    const model = selectedAgent?.voice_to_voice?.model;
                    const speedCfg = configData?.voice_to_voice?.[provider]?.[model]?.speed;
                    const speedAvailable = speedCfg?.available === 1;
                    const currentSpeed = typeof selectedAgent?.voice_to_voice?.speed === 'number' ? selectedAgent.voice_to_voice.speed : (speedCfg?.default_value ?? 1.0);
                    return speedAvailable ? (
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min={speedCfg.min_value}
                          max={speedCfg.max_value}
                          step={0.05}
                          value={currentSpeed}
                          onChange={(e) => updateSelectedAgent({ ...selectedAgent, voice_to_voice: { ...selectedAgent.voice_to_voice, speed: parseFloat(e.target.value) } })}
                          className="w-full"
                        />
                        <div className="text-sm text-gray-600" style={{ width: '60px', textAlign: 'right' }}>{currentSpeed.toFixed(2)}x</div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          min={0.1}
                          max={5}
                          step={0.1}
                          value={typeof selectedAgent?.voice_to_voice?.speed === 'number' ? selectedAgent.voice_to_voice.speed : 1.0}
                          onChange={(e) => updateSelectedAgent({ ...selectedAgent, voice_to_voice: { ...selectedAgent.voice_to_voice, speed: parseFloat(e.target.value) || 1.0 } })}
                          className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500"
                        />
                      </div>
                    );
                  })()}
                </div>
                {renderTranscriptionControls()}
              </ConfigurationSection>
            )}

            {!isVoiceToVoice && (
            <ConfigurationSection title="Transcription" hasErrors={hasConfigurationError("transcription")}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                  <ModernTimeDropdown value={selectedAgent?.transcription?.provider || ''} onChange={(e) => {
                    const newProvider = e.target.value; updateTranscriptionModelOptions(newProvider);
                    let firstModel = ''; if (configData.transcription && configData.transcription[newProvider]) { const models = Object.keys(configData.transcription[newProvider]); if (models.length > 0) { firstModel = models[0]; } }
                    updateSelectedAgent({ ...selectedAgent, transcription: { ...selectedAgent.transcription, provider: newProvider, model: firstModel } });
                  }} options={transcriptionProviderOptions} width="w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                  <ModernTimeDropdown value={selectedAgent?.transcription?.model || ''} onChange={(e) => updateSelectedAgent({ ...selectedAgent, transcription: { ...selectedAgent.transcription, model: e.target.value } })} options={transcriptionModelOptions} width="w-full" />
                  {selectedAgent?.transcription?.provider && configData.transcription && configData.transcription[selectedAgent.transcription.provider] && selectedAgent?.transcription?.model && configData.transcription[selectedAgent.transcription.provider][selectedAgent.transcription.model] && (
                    <div className="mt-1 text-xs text-gray-500">Latency: {configData.transcription[selectedAgent.transcription.provider][selectedAgent.transcription.model].latency}</div>
                  )}
                </div>
              </div>
              {renderTranscriptionControls()}
              <div className="pt-6">
                <div>
                <TextareaWithCharacterCount
                    label="Keywords"
                    value={selectedAgent?.transcription?.keywords}
                    onChange={(e) => updateSelectedAgent({ ...selectedAgent, transcription: { ...selectedAgent.transcription, keywords: e.target.value } })}
                    placeholder="Words that will frequently appear to capture (comma separated)"
                    rows={3}
                    maxLength={CHARACTER_LIMITS.KEYWORDS}
                  />
                </div>
              </div>
            </ConfigurationSection>
            )}

            {!isVoiceToVoice && (
            <ConfigurationSection title="Voice" hasErrors={hasConfigurationError("voice", "background_effects")}>

              <div className="flex justify-between items-center">

                <div className='p-2 bg-indigo-50 rounded-lg text-sm text-indigo-700 cursor-pointer' onClick={() => setShowVoicesModal(true)}>Explore Voices</div>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                    <ModernTimeDropdown value={selectedAgent?.voice?.provider || ''} onChange={(e) => {
                      const newProvider = e.target.value; updateVoiceModelOptions(newProvider);
                      let firstModel = ''; if (configData.voice && configData.voice[newProvider]) { const models = Object.keys(configData.voice[newProvider]); if (models.length > 0) { firstModel = models[0]; updateVoiceOptions(newProvider, firstModel, selectedVoiceGender); } }
                      const firstVoice = getFirstVoiceId(newProvider, firstModel, selectedVoiceGender);
                      const speedCfg = configData?.voice?.[newProvider]?.[firstModel]?.speed;
                      const resolvedSpeed = speedCfg?.available === 1 ? (typeof speedCfg.default_value === 'number' ? speedCfg.default_value : 1) : undefined;
                      updateSelectedAgent({ ...selectedAgent, voice: { ...selectedAgent.voice, provider: newProvider, model: firstModel, voice: firstVoice, gender: selectedVoiceGender, ...(resolvedSpeed !== undefined ? { speed: resolvedSpeed } : {}) } });
                    }} options={voiceProviderOptions} width="w-full" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                    <ModernTimeDropdown value={selectedAgent?.voice?.model || ''} onChange={(e) => {
                      const newModel = e.target.value; updateVoiceOptions(selectedAgent?.voice?.provider, newModel, selectedVoiceGender);
                      const firstVoice = getFirstVoiceId(selectedAgent?.voice?.provider, newModel, selectedVoiceGender);
                      const speedCfg = configData?.voice?.[selectedAgent?.voice?.provider]?.[newModel]?.speed;
                      const resolvedSpeed = speedCfg?.available === 1 ? (typeof speedCfg.default_value === 'number' ? speedCfg.default_value : 1) : undefined;
                      updateSelectedAgent({ ...selectedAgent, voice: { ...selectedAgent.voice, model: newModel, voice: firstVoice, ...(resolvedSpeed !== undefined ? { speed: resolvedSpeed } : {}) } });
                    }} options={voiceModelOptions} width="w-full" />
                    {selectedAgent?.voice?.provider && configData.voice && configData.voice[selectedAgent.voice.provider] && selectedAgent?.voice?.model && configData.voice[selectedAgent.voice.provider][selectedAgent.voice.model] && (
                      <div className="mt-1 text-xs text-gray-500">Latency: {configData.voice[selectedAgent.voice.provider][selectedAgent.voice.model].latency}</div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                    <ModernTimeDropdown value={selectedVoiceGender} onChange={(e) => {
                      const newGender = e.target.value; setSelectedVoiceGender(newGender); updateVoiceOptions(selectedAgent?.voice?.provider, selectedAgent?.voice?.model, newGender);
                      const firstVoice = getFirstVoiceId(selectedAgent?.voice?.provider, selectedAgent?.voice?.model, newGender);
                      updateSelectedAgent({ ...selectedAgent, voice: { ...selectedAgent.voice, voice: firstVoice, gender: newGender } }); // Always reset to first voice when gender changes
                    }} options={voiceGenderOptions} width="w-full" />
                  </div>
                </div>
                <div className="grid grid-cols-1">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Voice</label>
                    <ModernTimeDropdown
                      value={getVoiceNameFromId(
                        selectedAgent?.voice?.provider,
                        selectedAgent?.voice?.model,
                        selectedAgent?.voice?.gender || selectedVoiceGender,
                        selectedAgent?.voice?.voice
                      )}
                      onChange={(e) => {
                        const voiceName = e.target.value;
                        const voiceId = getVoiceIdFromName(
                          selectedAgent?.voice?.provider,
                          selectedAgent?.voice?.model,
                          selectedAgent?.voice?.gender || selectedVoiceGender,
                          voiceName
                        );
                        updateSelectedAgent({
                          ...selectedAgent,
                          voice: {
                            ...selectedAgent.voice,
                            voice: voiceId
                          }
                        });
                      }}
                      options={voiceOptions}
                      width="w-full"
                    />
                  </div>
                </div>
                {/* Speed control */}
                <div className="grid grid-cols-1">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Speed</label>
                    {selectedAgent?.voice?.provider && selectedAgent?.voice?.model && configData?.voice?.[selectedAgent.voice.provider]?.[selectedAgent.voice.model]?.speed?.available === 1 ? (
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min={configData.voice[selectedAgent.voice.provider][selectedAgent.voice.model].speed.min_value}
                          max={configData.voice[selectedAgent.voice.provider][selectedAgent.voice.model].speed.max_value}
                          step={0.05}
                          value={typeof selectedAgent?.voice?.speed === 'number' ? selectedAgent.voice.speed : (configData.voice[selectedAgent.voice.provider][selectedAgent.voice.model].speed.default_value || 1)}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            updateSelectedAgent({ ...selectedAgent, voice: { ...selectedAgent.voice, speed: val } });
                          }}
                          className="w-full"
                        />
                        <div className="text-sm text-gray-600" style={{ width: '60px', textAlign: 'right' }}>{(typeof selectedAgent?.voice?.speed === 'number' ? selectedAgent.voice.speed : (configData.voice[selectedAgent.voice.provider][selectedAgent.voice.model].speed.default_value || 1)).toFixed(2)}x</div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">Not available for this model</div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Enable TTS Caching</label>
                    <ModernTimeDropdown
                      value={(selectedAgent?.voice?.tts_cache ?? 1) === 1 ? 'Yes' : 'No'}
                      onChange={(e) => updateSelectedAgent({ ...selectedAgent, voice: { ...selectedAgent.voice, tts_cache: e.target.value === 'Yes' ? 1 : 0 } })}
                      options={['Yes', 'No']}
                      width="w-full"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Background noise</label>
                    <ModernTimeDropdown value={selectedAgent?.background_effects?.noise || ''} onChange={(e) => updateSelectedAgent({ ...selectedAgent, background_effects: { ...(selectedAgent?.background_effects || {}), noise: e.target.value } })} options={configData?.background_effects?.noise || []} width="w-full" />
                  </div>
                </div>
          <div className="grid grid-cols-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Background noise volume</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={typeof selectedAgent?.background_effects?.noise_volume === 'number' ? selectedAgent.background_effects.noise_volume : 50}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    updateSelectedAgent({ ...selectedAgent, background_effects: { ...(selectedAgent?.background_effects || {}), noise_volume: val } });
                  }}
                  className="w-full"
                />
                <div className="text-sm text-gray-600" style={{ width: '60px', textAlign: 'right' }}>
                  {(typeof selectedAgent?.background_effects?.noise_volume === 'number' ? selectedAgent.background_effects.noise_volume : 50).toFixed(0)}%
                </div>
              </div>
            </div>
          </div>
              </div>
            </ConfigurationSection>
            )}
          </div>
          <div id="agent-panel-tools" role="tabpanel" aria-labelledby="agent-tab-tools" hidden={agentDetailsTab !== 'tools'} className="p-6 space-y-6 [&>:first-child]:border-t-0 [&>:first-child]:pt-0">
            <StandardToolsConfig
                availableLanguages={getAvailableLanguageKeys(selectedAgent)}
              value={selectedAgent?.conversation_controls}
              pipelineType={selectedAgent?.pipeline_type}
              onChange={(conversationControls) => updateSelectedAgent({
                ...selectedAgent,
                conversation_controls: conversationControls,
              })}
            />

            <CustomToolsConfig
              tools={selectedAgent?.custom_tools || []}
              onChange={(next) => updateSelectedAgent({ ...selectedAgent, custom_tools: next })}
              availableLanguages={getAvailableLanguageKeys(selectedAgent)}
              systemVariables={systemVariables}
              dynamicVariables={dynamicVariables}
              onAddDynamicVariable={handleAddDynamicVariable}
              llmModelOptions={configData?.custom_tool_llm || {}}
            />

            <ConfigurationSection title="Knowledge Base" hasErrors={hasConfigurationError("knowledge_base")}>
              <div className="mb-4"><ToolAvailability availableLanguages={getAvailableLanguageKeys(selectedAgent)} value={selectedAgent?.knowledge_base?.availability} onChange={availability => updateSelectedAgent({ ...selectedAgent, knowledge_base: { ...selectedAgent?.knowledge_base, availability } })} /></div>

              <ModernTimeDropdown
                isMulti
                options={knowledgeBaseNames}
                value={getSelectedKnowledgeBaseNames()}
                onChange={handleKnowledgeBaseChange}
                width="w-full"
              />
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <MessageWithOverrides
                    label="Start Message"
                    value={selectedAgent?.knowledge_base?.start_message || ''}
                    onChange={(e) => updateSelectedAgent({ ...selectedAgent, knowledge_base: { ...(selectedAgent?.knowledge_base || {}), start_message: e.target.value } })}
                    placeholder="Optional: message to speak before using the knowledge base"
                    rows={2}
                    systemVariables={systemVariables}
                    dynamicVariables={dynamicVariables}
                    onAddDynamicVariable={handleAddDynamicVariable}
                    overrideMaxLength={500}
                    rules={getMessageRules(selectedAgent?.knowledge_base, 'start_message', ['voice', 'chat'])}
                    modes={['voice', 'chat']}
                    availableLanguages={getAvailableLanguageKeys(selectedAgent)}
                    onRulesChange={rules => updateSelectedAgent({ ...selectedAgent, knowledge_base: { ...selectedAgent?.knowledge_base, start_message_rules: rules } })}
                  />

                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Message Delay (seconds)</label>
                  <input type="number" min={0} max={44} step="any" value={selectedAgent?.knowledge_base?.start_delay_seconds ?? 0} onChange={e => updateSelectedAgent({ ...selectedAgent, knowledge_base: { ...selectedAgent?.knowledge_base, start_delay_seconds: e.target.value === '' ? 0 : Number(e.target.value) } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  <p className="text-xs text-gray-500 mt-1">Voice retrieval starts immediately; 0 preserves the immediate start message.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sound during Start Message Delay</label>
                  <ModernTimeDropdown ariaLabel="Sound during Start Message Delay" value={selectedAgent?.knowledge_base?.start_sound ?? 'none'} onChange={e => updateSelectedAgent({ ...selectedAgent, knowledge_base: { ...selectedAgent?.knowledge_base, start_sound: e.target.value } })} options={[['none', 'None'], ['typing', 'Typing only'], ['typing_background', 'Typing with background noise']]} width="w-full" />
                  <p className="text-xs text-gray-500 mt-1">Typing requires a positive Start delay and Delay Execution set to No.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Interrupt start message when tool finishes</label>
                  <ModernTimeDropdown ariaLabel="Interrupt start message when tool finishes" value={String(selectedAgent?.knowledge_base?.interrupt_start_message_on_result ?? false)} onChange={e => updateSelectedAgent({ ...selectedAgent, knowledge_base: { ...selectedAgent?.knowledge_base, interrupt_start_message_on_result: e.target.value === 'true' } })} options={[['false', 'No'], ['true', 'Yes']]} width="w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Delay Execution till start message has spoken</label>
                  <ModernTimeDropdown
                    value={
                      (selectedAgent?.knowledge_base?.delay_till_start_message_spoken === 'Yes' ||
                       selectedAgent?.knowledge_base?.delay_till_start_message_spoken === true)
                        ? 'Yes'
                        : 'No'
                    }
                    onChange={(e) => updateSelectedAgent({ ...selectedAgent, knowledge_base: { ...(selectedAgent?.knowledge_base || {}), delay_till_start_message_spoken: e.target.value } })}
                    options={['No', 'Yes']}
                    width="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cancel on interruption</label>
                  <ModernTimeDropdown
                    value={normalizeBooleanFlag(selectedAgent?.knowledge_base?.cancel_on_interruption, false) ? 'Yes' : 'No'}
                    onChange={(e) => updateSelectedAgent({
                      ...selectedAgent,
                      knowledge_base: {
                        ...(selectedAgent?.knowledge_base || {}),
                        cancel_on_interruption: e.target.value === 'Yes'
                      }
                    })}
                    options={['Yes', 'No']}
                    width="w-full"
                  />
                </div>
              </div>
            </ConfigurationSection>
          </div>
          <div id="agent-panel-conversation_controls" role="tabpanel" aria-labelledby="agent-tab-conversation_controls" hidden={agentDetailsTab !== 'conversation_controls'} className="p-6 space-y-6 [&>:first-child]:border-t-0 [&>:first-child]:pt-0">
            <ConfigurationSection title="Conversation Hangup" hasErrors={hasConfigurationError("conversation_hangup")}>
              <div className="mb-4"><ToolAvailability availableLanguages={getAvailableLanguageKeys(selectedAgent)} value={selectedAgent?.conversation_hangup?.availability} onChange={availability => updateSelectedAgent({ ...selectedAgent, conversation_hangup: { ...selectedAgent?.conversation_hangup, availability } })} /></div>

              <div className="flex gap-6">
                <div className="w-1/2">
                <TextareaWithCharacterCount
                    label="Hangup based on Intent (Instructions)"
                    value={selectedAgent?.conversation_hangup?.hangup_based_on_intent_instruction}
                    onChange={(e) => updateSelectedAgent({ ...selectedAgent, conversation_hangup: { ...selectedAgent?.conversation_hangup, hangup_based_on_intent_instruction: e.target.value } })}
                    placeholder="Provide instructions to determine when to hang up"
                    rows={3}
                    maxLength={CHARACTER_LIMITS.HANGUP_INSTRUCTION}
                  />
                  <p className="text-xs text-gray-500 mt-1">None/Empty means call will not be disconnected based on this condition.</p>
                </div>
                <div className="w-1/2">
                {/* <TextareaWithCharacterCount
                    label="Hangup Message"
                    value={selectedAgent?.conversation_hangup?.hangup_message}
                    onChange={(e) => updateSelectedAgent({ ...selectedAgent, conversation_hangup: { ...selectedAgent?.conversation_hangup, hangup_message: e.target.value } })}
                    placeholder="Closing message when agent hangs up"
                    rows={2}
                    maxLength={CHARACTER_LIMITS.HANGUP_MESSAGE}
                  /> */}
                  <MessageWithOverrides
                    label="Hangup Message"
                    value={selectedAgent?.conversation_hangup?.hangup_message}
                    onChange={(e) => updateSelectedAgent({ ...selectedAgent, conversation_hangup: { ...selectedAgent?.conversation_hangup, hangup_message: e.target.value } })}
                    placeholder="Closing message when agent hangs up"
                    rows={2}
                    maxLength={CHARACTER_LIMITS.HANGUP_MESSAGE}
                    systemVariables={systemVariables}
                    dynamicVariables={dynamicVariables}
                    onAddDynamicVariable={handleAddDynamicVariable}
                    overrideMaxLength={CHARACTER_LIMITS.HANGUP_MESSAGE}
                    rules={getAgentMessageRules(selectedAgent, 'conversation_hangup', 'hangup_message')}
                    modes={['voice', 'chat']}
                    availableLanguages={getAvailableLanguageKeys(selectedAgent)}
                    onRulesChange={rules => updateSelectedAgent(setAgentMessageRules(selectedAgent, 'conversation_hangup', 'hangup_message', rules))}
                  />

                </div>
              </div>
            </ConfigurationSection>

            <ConfigurationSection title="Conversation Turn" hasErrors={hasConfigurationError("conversation_turn_timeout")}>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Turn Timeout - seconds</label>
                  <input type="number" value={selectedAgent?.conversation_turn_timeout?.turn_timeout_seconds ?? ''} onChange={(e) => updateSelectedAgent({ ...selectedAgent, conversation_turn_timeout: { ...selectedAgent?.conversation_turn_timeout, turn_timeout_seconds: e.target.value === '' ? '' : parseInt(e.target.value) } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500" placeholder="" />
                  <p className="text-xs text-gray-500 mt-1">Seconds of user inactivity before a reminder in calls and chats. Leave empty to disable reminders and idle closure.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Attempts</label>
                  <input type="number" value={selectedAgent?.conversation_turn_timeout?.max_tries_for_conversation_turn_timeout ?? ''} onChange={(e) => updateSelectedAgent({ ...selectedAgent, conversation_turn_timeout: { ...selectedAgent?.conversation_turn_timeout, max_tries_for_conversation_turn_timeout: e.target.value === '' ? null : parseInt(e.target.value) } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500" placeholder="" />
                </div>
                <div>
                {/* <TextareaWithCharacterCount
                    label="Message"
                    value={selectedAgent?.conversation_turn_timeout?.turn_timeout_message}
                    onChange={(e) => updateSelectedAgent({ ...selectedAgent, conversation_turn_timeout: { ...selectedAgent?.conversation_turn_timeout, turn_timeout_message: e.target.value } })}
                    placeholder="Message to use when a turn times out"
                    rows={3}
                    maxLength={CHARACTER_LIMITS.TURN_TIMEOUT_MESSAGE}
                  /> */}
                  <MessageWithOverrides
                    label="Message"
                    value={selectedAgent?.conversation_turn_timeout?.turn_timeout_message}
                    onChange={(e) => updateSelectedAgent({ ...selectedAgent, conversation_turn_timeout: { ...selectedAgent?.conversation_turn_timeout, turn_timeout_message: e.target.value } })}
                    placeholder="Message to use when a turn times out"
                    rows={3}
                    maxLength={CHARACTER_LIMITS.TURN_TIMEOUT_MESSAGE}
                    systemVariables={systemVariables}
                    dynamicVariables={dynamicVariables}
                    onAddDynamicVariable={handleAddDynamicVariable}
                    overrideMaxLength={CHARACTER_LIMITS.TURN_TIMEOUT_MESSAGE}
                    rules={getMessageRules(selectedAgent?.conversation_turn_timeout, 'turn_timeout_message', ['voice', 'chat'])}
                    modes={['voice', 'chat']}
                    availableLanguages={getAvailableLanguageKeys(selectedAgent)}
                    onRulesChange={rules => updateSelectedAgent({ ...selectedAgent, conversation_turn_timeout: { ...selectedAgent?.conversation_turn_timeout, turn_timeout_message_rules: rules } })}
                  />

                </div>
                <div>
                  <MessageWithOverrides
                    label="Last Message"
                    value={selectedAgent?.conversation_turn_timeout?.last_message || ''}
                    onChange={(e) => updateSelectedAgent({ ...selectedAgent, conversation_turn_timeout: { ...selectedAgent?.conversation_turn_timeout, last_message: e.target.value } })}
                    placeholder="Message before the call or chat ends"
                    rows={3}
                    maxLength={CHARACTER_LIMITS.TURN_TIMEOUT_MESSAGE}
                    systemVariables={systemVariables}
                    dynamicVariables={dynamicVariables}
                    onAddDynamicVariable={handleAddDynamicVariable}
                    overrideMaxLength={CHARACTER_LIMITS.TURN_TIMEOUT_MESSAGE}
                    rules={getMessageRules(selectedAgent?.conversation_turn_timeout, 'last_message', ['voice', 'chat'])}
                    modes={['voice', 'chat']}
                    availableLanguages={getAvailableLanguageKeys(selectedAgent)}
                    onRulesChange={rules => updateSelectedAgent({ ...selectedAgent, conversation_turn_timeout: { ...selectedAgent?.conversation_turn_timeout, last_message_rules: rules } })}
                  />
                  <p className="text-xs text-gray-500 mt-1 mb-3">Sent when the agent ends the conversation or idle attempts are exhausted. The existing hangup message is used if this is empty.</p>

                </div>
              </div>
            </ConfigurationSection>

            <ConfigurationSection title="Call Transfer" hasErrors={hasConfigurationError("call_transfer")}>
              <div className="mb-4"><ToolAvailability availableLanguages={getAvailableLanguageKeys(selectedAgent)} value={selectedAgent?.call_transfer?.availability} onChange={availability => updateSelectedAgent({ ...selectedAgent, call_transfer: { ...selectedAgent?.call_transfer, availability } })} /></div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Enabled</label>
                  <ModernTimeDropdown
                    value={selectedAgent?.call_transfer?.enabled ?? 'No'}
                    onChange={(e) => updateSelectedAgent({
                      ...selectedAgent,
                      call_transfer: {
                        ...(selectedAgent?.call_transfer || {}),
                        enabled: e.target.value,
                        ...(e.target.value !== 'Yes' ? { instructions: '', enable_webhook: 'No', webhook_url: '', headers: '' } : {})
                      }
                    })}
                    options={["Yes", "No"]}
                    width="w-full"
                  />
                </div>

                {selectedAgent?.call_transfer?.enabled === 'Yes' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Transfer Type</label>
                    <ModernTimeDropdown
                      value={selectedAgent?.call_transfer?.transfer_type || 'Cold'}
                      onChange={(e) => updateSelectedAgent({
                        ...selectedAgent,
                        call_transfer: {
                          ...(selectedAgent?.call_transfer || {}),
                          transfer_type: e.target.value,
                          enabled: selectedAgent?.call_transfer?.enabled ?? 'Yes'
                        }
                      })}
                      options={["Warm", "Cold"]}
                      width="w-full"
                    />
                  </div>
                )}

                {selectedAgent?.call_transfer?.enabled === 'Yes' && (
                  <div className="md:col-span-2">
                    <TextareaWithVariables
                      label="Instructions"
                      value={selectedAgent?.call_transfer?.instructions || ''}
                      onChange={(e) => updateSelectedAgent({
                        ...selectedAgent,
                        call_transfer: {
                          ...(selectedAgent?.call_transfer || {}),
                          instructions: e.target.value,
                          enabled: selectedAgent?.call_transfer?.enabled ?? 'Yes'
                        }
                      })}
                      placeholder="Describe when and how to transfer the call. Use variables like {{customer_name}}."
                      rows={4}
                      systemVariables={systemVariables}
                      dynamicVariables={dynamicVariables}
                      onAddDynamicVariable={handleAddDynamicVariable}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                      <div className="md:col-span-2">
                        <MessageWithOverrides
                          label="Start Message"
                          value={selectedAgent?.call_transfer?.start_message || ''}
                          onChange={(e) => updateSelectedAgent({
                            ...selectedAgent,
                            call_transfer: {
                              ...(selectedAgent?.call_transfer || {}),
                              start_message: e.target.value,
                              enabled: selectedAgent?.call_transfer?.enabled ?? 'Yes'
                            }
                          })}
                          placeholder="Optional: message to speak before transferring the call"
                          rows={2}
                          systemVariables={systemVariables}
                          dynamicVariables={dynamicVariables}
                          onAddDynamicVariable={handleAddDynamicVariable}
                          overrideMaxLength={500}
                          rules={getAgentMessageRules(selectedAgent, 'call_transfer', 'start_message')}
                          modes={['voice', 'chat']}
                          availableLanguages={getAvailableLanguageKeys(selectedAgent)}
                          onRulesChange={rules => updateSelectedAgent(setAgentMessageRules(selectedAgent, 'call_transfer', 'start_message', rules))}
                        />

                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Accept start message from LLM</label>
                        <ModernTimeDropdown
                          value={selectedAgent?.call_transfer?.accept_start_message_from_llm ?? 'No'}
                          onChange={(e) => updateSelectedAgent({
                            ...selectedAgent,
                            call_transfer: {
                              ...(selectedAgent?.call_transfer || {}),
                              accept_start_message_from_llm: e.target.value,
                            }
                          })}
                          options={['No', 'Yes']}
                          width="w-full"
                        />
                        {selectedAgent?.call_transfer?.accept_start_message_from_llm === 'Yes' && (
                          <p className="mt-2 text-sm text-gray-500">
                            The LLM can pass <code>customer_hold_message</code> in the transfer tool to speak that text before transfer.
                            {' '}If omitted or empty, the configured start message is used.
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Delay Execution till start message has spoken</label>
                        <ModernTimeDropdown
                          value={
                            (selectedAgent?.call_transfer?.delay_till_start_message_spoken === 'Yes' ||
                             selectedAgent?.call_transfer?.delay_till_start_message_spoken === true)
                              ? 'Yes'
                              : 'No'
                          }
                          onChange={(e) => updateSelectedAgent({
                            ...selectedAgent,
                            call_transfer: {
                              ...(selectedAgent?.call_transfer || {}),
                              delay_till_start_message_spoken: e.target.value,
                              enabled: selectedAgent?.call_transfer?.enabled ?? 'Yes'
                            }
                          })}
                          options={['No', 'Yes']}
                          width="w-full"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Enable Webhook</label>
                        <ModernTimeDropdown
                          value={selectedAgent?.call_transfer?.enable_webhook ?? 'No'}
                          onChange={(e) => updateSelectedAgent({
                            ...selectedAgent,
                            call_transfer: {
                              ...(selectedAgent?.call_transfer || {}),
                              enable_webhook: e.target.value,
                              ...(e.target.value !== 'Yes' ? { webhook_url: '', headers: '' } : {}),
                              enabled: selectedAgent?.call_transfer?.enabled ?? 'Yes'
                            }
                          })}
                          options={['No', 'Yes']}
                          width="w-full"
                        />
                      </div>
                      {selectedAgent?.call_transfer?.enable_webhook === 'Yes' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Webhook URL</label>
                            <input
                              type="url"
                              value={selectedAgent?.call_transfer?.webhook_url || ''}
                              onChange={(e) => updateSelectedAgent({
                                ...selectedAgent,
                                call_transfer: {
                                  ...(selectedAgent?.call_transfer || {}),
                                  webhook_url: e.target.value,
                                  enabled: selectedAgent?.call_transfer?.enabled ?? 'Yes'
                                }
                              })}
                              placeholder="https://your-api.com/call-transfer-webhook"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Headers (semicolon-separated key:value; key2:value2)</label>
                            <input
                              type="text"
                              value={selectedAgent?.call_transfer?.headers || ''}
                              onChange={(e) => {
                                const raw = String(e.target.value || '');
                                const parts = raw
                                  .split(';')
                                  .map(s => s.trim())
                                  .filter(Boolean)
                                  .map(p => p.replace(/\s*:\s*/g, ':'));
                                const normalized = parts.join(';');
                                updateSelectedAgent({
                                  ...selectedAgent,
                                  call_transfer: {
                                    ...(selectedAgent?.call_transfer || {}),
                                    headers: normalized,
                                    enabled: selectedAgent?.call_transfer?.enabled ?? 'Yes'
                                  }
                                });
                              }}
                              placeholder="Authorization:Bearer abc; X-Trace-Id:123"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </ConfigurationSection>

            <ConfigurationSection title="Conversation Termination" hasErrors={hasConfigurationError("conversation_termination")}>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Conversation Duration - seconds</label>
                  <input type="number" value={selectedAgent?.conversation_termination?.maximum_conversation_duration ?? ''} onChange={(e) => updateSelectedAgent({ ...selectedAgent, conversation_termination: { ...selectedAgent?.conversation_termination, maximum_conversation_duration: e.target.value === '' ? '' : parseInt(e.target.value) } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500" placeholder="" />
                </div>
                <div>
                <MessageWithOverrides
                    label="Termination Message"
                    value={selectedAgent?.conversation_termination?.termination_message}
                    onChange={(e) => updateSelectedAgent({ ...selectedAgent, conversation_termination: { ...selectedAgent?.conversation_termination, termination_message: e.target.value } })}
                    placeholder="Message to play/send when terminating the conversation"
                    rows={3}
                    maxLength={CHARACTER_LIMITS.TERMINATION_MESSAGE}
                  overrideMaxLength={CHARACTER_LIMITS.TERMINATION_MESSAGE}
                  rules={getMessageRules(selectedAgent?.conversation_termination, 'termination_message', ['voice', 'chat'])}
                  modes={['voice', 'chat']}
                  availableLanguages={getAvailableLanguageKeys(selectedAgent)}
                  onRulesChange={rules => updateSelectedAgent({ ...selectedAgent, conversation_termination: { ...selectedAgent?.conversation_termination, termination_message_rules: rules } })}
                  systemVariables={systemVariables}
                  dynamicVariables={dynamicVariables}
                  onAddDynamicVariable={handleAddDynamicVariable}
                />

                </div>
              </div>
            </ConfigurationSection>

            <ConfigurationSection title="Interruptions" hasErrors={hasConfigurationError("interruptions")}>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minimum number of words</label>
                  <input type="number" value={selectedAgent?.interruptions?.minimum_number_of_words ?? ''} onChange={(e) => updateSelectedAgent({ ...selectedAgent, interruptions: { ...selectedAgent?.interruptions, minimum_number_of_words: e.target.value === '' ? '' : parseInt(e.target.value) } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500" placeholder="" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minimum speech seconds</label>
                  <input
                    type="number"
                    min={0}
                    step={0.1}
                    value={selectedAgent?.interruptions?.minimum_speech_secs ?? ''}
                    onChange={(e) => updateSelectedAgent({
                      ...selectedAgent,
                      interruptions: {
                        ...selectedAgent?.interruptions,
                        minimum_speech_secs: e.target.value === '' ? '' : parseFloat(e.target.value)
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500"
                    placeholder=""
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Interruption allowed for agent first message</label>
                  <ModernTimeDropdown value={selectedAgent?.interruptions?.interruption_allowed_for_agent_first_message ?? ''} onChange={(e) => updateSelectedAgent({ ...selectedAgent, interruptions: { ...selectedAgent?.interruptions, interruption_allowed_for_agent_first_message: e.target.value } })} options={["Yes", "No"]} width="w-full" />
                </div>
              </div>
            </ConfigurationSection>

            <ConfigurationSection title="Voice Mail" hasErrors={hasConfigurationError("voice_mail")}>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
                  <ModernTimeDropdown value={selectedAgent?.voice_mail?.voice_mail_action ?? 'Hangup'} onChange={(e) => updateSelectedAgent({ ...selectedAgent, voice_mail: { ...selectedAgent?.voice_mail, voice_mail_action: e.target.value } })} options={["Hangup", "Leave A Message"]} width="w-full" />
                </div>
                <div>
                <MessageWithOverrides
                    rows={1}
                    label="Leave Message"
                    value={selectedAgent?.voice_mail?.voice_mail_action === 'Leave A Message' ? selectedAgent?.voice_mail?.voice_mail_leave_message : ''}
                    onChange={(e) => updateSelectedAgent({ ...selectedAgent, voice_mail: { ...selectedAgent?.voice_mail, voice_mail_leave_message: e.target.value } })}
                    disabled={selectedAgent?.voice_mail?.voice_mail_action !== 'Leave A Message'}
                    placeholder=""
                    maxLength={CHARACTER_LIMITS.VOICE_MAIL_MESSAGE}
                  overrideMaxLength={CHARACTER_LIMITS.VOICE_MAIL_MESSAGE}
                  rules={getMessageRules(selectedAgent?.voice_mail, 'voice_mail_leave_message', ['voice'])}
                  modes={['voice']}
                  availableLanguages={getAvailableLanguageKeys(selectedAgent)}
                  onRulesChange={rules => updateSelectedAgent({ ...selectedAgent, voice_mail: { ...selectedAgent?.voice_mail, voice_mail_leave_message_rules: rules } })}
                  systemVariables={systemVariables}
                  dynamicVariables={dynamicVariables}
                  onAddDynamicVariable={handleAddDynamicVariable}
                />

                </div>
              </div>
            </ConfigurationSection>

            <ConfigurationSection title="DTMF" hasErrors={hasConfigurationError("dtmf_enabled", "dtmf_idle_timeout_seconds")}>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Enabled</label>
                  <ModernTimeDropdown
                    value={selectedAgent?.dtmf_enabled ?? 'No'}
                    onChange={(e) => updateSelectedAgent({ ...selectedAgent, dtmf_enabled: e.target.value })}
                    options={["Yes", "No"]}
                    width="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Idle Timeout (seconds)</label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={selectedAgent?.dtmf_idle_timeout_seconds ?? 2}
                    onChange={(e) => updateSelectedAgent({
                      ...selectedAgent,
                      dtmf_idle_timeout_seconds: e.target.value === '' ? '' : parseInt(e.target.value, 10)
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500"
                  />
                </div>
              </div>
            </ConfigurationSection>

            <ConfigurationSection title="Post Call Analytics" hasErrors={hasConfigurationError("post_call_analytics_enabled", "post_call_analytics_service_id", "post_call_analytics_transcription_mode")}>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Enabled</label>
                  <ModernTimeDropdown
                    value={selectedAgent?.post_call_analytics_enabled ?? 'No'}
                    onChange={(e) => updateSelectedAgent({
                      ...selectedAgent,
                      post_call_analytics_enabled: e.target.value,
                      post_call_analytics_transcription_mode: selectedAgent?.post_call_analytics_transcription_mode || configData?.post_call_analytics_transcription_mode?.[0] || ''
                    })}
                    options={["Yes", "No"]}
                    width="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service ID</label>
                  <ModernTimeDropdown 
                    isDisabled={selectedAgent?.post_call_analytics_enabled !== 'Yes'}
                    value={selectedAgent?.post_call_analytics_service_id ?? ''}
                    onChange={(e) => {
                      if (selectedAgent?.post_call_analytics_enabled !== 'Yes') return;
                      updateSelectedAgent({ ...selectedAgent, post_call_analytics_service_id: e.target.value })
                    }}
                    options={serviceOptions}
                    width="w-full" />
                </div>
                {selectedAgent?.post_call_analytics_enabled === 'Yes' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Transcription Mode</label>
                    <ModernTimeDropdown
                      value={selectedAgent?.post_call_analytics_transcription_mode || configData?.post_call_analytics_transcription_mode?.[0] || ''}
                      onChange={(e) => updateSelectedAgent({
                        ...selectedAgent,
                        post_call_analytics_transcription_mode: e.target.value
                      })}
                      options={configData?.post_call_analytics_transcription_mode || []}
                      width="w-full"
                    />
                  </div>
                )}
              </div>
            </ConfigurationSection>
          </div>
          <div id="agent-panel-chat_controls" role="tabpanel" aria-labelledby="agent-tab-chat_controls" hidden={agentDetailsTab !== 'chat_controls'} className="p-6 space-y-6 [&>:first-child]:border-t-0 [&>:first-child]:pt-0">
            <ChatSettings
              agent={selectedAgent}
              allowedConfig={configData}
              onChange={chat => updateSelectedAgent({ ...selectedAgent, chat })}
            />
          </div>
          <div id="agent-panel-inbuilt_analytics" role="tabpanel" aria-labelledby="agent-tab-inbuilt_analytics" hidden={agentDetailsTab !== 'inbuilt_analytics'} className="p-6 [&>:first-child]:border-t-0 [&>:first-child]:pt-0">
              <InbuiltAnalyticsConfig
                value={inbuiltAnalyticsConfig}
                onChange={setInbuiltAnalyticsConfig}
                showToast={showToast}
                systemVariables={systemVariables}
                reservedNames={Object.keys(configData?.inbuilt_analytics_system_parameters || {})}
                clientId={client_id}
                serviceId={service_id}
              />
            </div>
          <div id="agent-panel-future_scheduling" role="tabpanel" aria-labelledby="agent-tab-future_scheduling" hidden={agentDetailsTab !== 'future_scheduling'} className="p-6">
            <FutureSchedulingConfig
              value={futureSchedulingConfig}
              onChange={setFutureSchedulingConfig}
              analyticsConfig={inbuiltAnalyticsConfig}
              availableBots={activeAgentOptions}
              showToast={showToast}
            />
          </div>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center" style={{ height: '88%', flexDirection: 'column' }}>
          <div className="loader"></div>
          <div style={{textAlign: 'center'}}>Loading <br/> Please Wait</div>
        </div>
      )}

      {showVoicesModal && (
        <div style={{position: 'fixed', left: '0%', top: '0%', width: '100%', height: '100%', background: 'rgba(0, 0, 0, 0.5)', zIndex: '1001'}}>
          <div style={{position: 'fixed', left: '2%', top: '3.5%', width: '96%', height: '93%', background: 'white', borderRadius: '10px', boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 3px', zIndex: '999999999', padding: '5px'}}>
            <div style={{position: 'absolute', right: '10px', top: '10px', cursor: 'pointer'}} onClick={() => { setShowVoicesModal(false); }}><FaTimes style={{color: 'slategray'}}/></div>
            <div style={{overflow: 'auto', height: '100%'}}>
              <ExploreVoicesModal />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentDetails;
